package org.gymly.ui.admin

import org.gymly.util.GymlyCurrency

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.FileDownload
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminClient
import org.gymly.model.AdminEnrollmentReport
import org.gymly.model.AdminInventoryReport
import org.gymly.model.AdminProductCategoryItem
import org.gymly.model.AdminProductStockLevel
import org.gymly.model.AdminCashMethodAmount
import org.gymly.model.AdminCashReconciliationReport
import org.gymly.model.AdminCashReconciliationRow
import org.gymly.model.AdminExchangeRateQuote
import org.gymly.model.AdminMembershipPlanItem
import org.gymly.model.AdminMembershipReport
import org.gymly.model.AdminMembershipReportRow
import org.gymly.model.AdminPaymentMethod
import org.gymly.model.AdminPaymentMethodTotal
import org.gymly.model.AdminProduct
import org.gymly.model.AdminReportExportFormat
import org.gymly.model.AdminReportFilters
import org.gymly.model.AdminReportKind
import org.gymly.model.AdminSalesReport
import org.gymly.model.AdminSalesReportRow
import org.gymly.model.AdminSessionReport
import org.gymly.model.AdminSessionReportRow
import org.gymly.model.AdminStoreSaleMethod
import org.gymly.model.AdminStoreSaleStatus
import org.gymly.model.AdminTrainer
import org.gymly.model.AdminUser
import org.gymly.model.allowedReports
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.util.downloadBytes
import org.gymly.util.formatMoney
import org.gymly.ui.components.AdminDateField
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.AdminDateOutputFormat
import org.gymly.util.isoDateToUtcEpochMillis
import org.gymly.util.todayIsoDate
import org.gymly.util.utcEpochMillisToIsoDate
import org.gymly.util.userMessage

/** Filas por página en el detalle de todos los reportes. */
private const val REPORTS_PAGE_SIZE = ADMIN_LIST_PAGE_SIZE

private fun todayIso(): String =
    todayIsoDate().ifBlank { "2026-01-01" }

/** Resta o suma días a una fecha ISO (sin zonas horarias: se opera en medianoche UTC). */
private fun shiftIso(iso: String, days: Int): String {
    val base = isoDateToUtcEpochMillis(iso) ?: return iso
    return utcEpochMillisToIsoDate(base + days * 86_400_000L)
}

private fun parseLooseDate(raw: String): String? {
    val t = raw.trim()
    if (Regex("""^\d{4}-\d{2}-\d{2}$""").matches(t)) return t
    val m = Regex("""^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$""").matchEntire(t) ?: return null
    val day = m.groupValues[1].toIntOrNull() ?: return null
    val month = m.groupValues[2].toIntOrNull() ?: return null
    val year = m.groupValues[3].toIntOrNull() ?: return null
    if (month !in 1..12 || day !in 1..31) return null
    return "$year-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
}

private fun displayDate(iso: String): String {
    val parts = iso.split("-")
    if (parts.size != 3) return iso
    return "${parts[2]}-${parts[1]}-${parts[0]}"
}

@Composable
fun AdminReportsScreen(user: AdminUser) {
    val allowed = user.allowedReports()
    val scope = rememberCoroutineScope()
    var kind by remember { mutableStateOf(AdminReportKind.CASH_RECONCILIATION) }
    var trainers by remember { mutableStateOf<List<AdminTrainer>>(emptyList()) }
    var clients by remember { mutableStateOf<List<AdminClient>>(emptyList()) }
    var products by remember { mutableStateOf<List<AdminProduct>>(emptyList()) }
    var categories by remember { mutableStateOf<List<AdminProductCategoryItem>>(emptyList()) }
    var plans by remember { mutableStateOf<List<AdminMembershipPlanItem>>(emptyList()) }
    var trainerId by remember { mutableStateOf<String?>(null) }
    var productId by remember { mutableStateOf<String?>(null) }
    var categoryId by remember { mutableStateOf<String?>(null) }
    var stockLevel by remember { mutableStateOf<String?>(null) }
    var planId by remember { mutableStateOf<String?>(null) }
    var clientId by remember { mutableStateOf<String?>(null) }
    var staffId by remember { mutableStateOf<String?>(null) }
    var subjectType by remember { mutableStateOf<String?>(null) }
    var saleStatus by remember { mutableStateOf<String?>(null) }
    var saleMethod by remember { mutableStateOf<String?>(null) }
    var clientStatus by remember { mutableStateOf<String?>(null) }
    var membership by remember { mutableStateOf<String?>(null) }
    // Al abrir o refrescar: todos los reportes empiezan con la fecha de hoy.
    var fromIso by remember { mutableStateOf(todayIso()) }
    var toIso by remember { mutableStateOf(todayIso()) }
    var fromInput by remember { mutableStateOf(displayDate(fromIso)) }
    var toInput by remember { mutableStateOf(displayDate(toIso)) }
    var sessionReport by remember { mutableStateOf<AdminSessionReport?>(null) }
    var membershipReport by remember { mutableStateOf<AdminMembershipReport?>(null) }
    var salesReport by remember { mutableStateOf<AdminSalesReport?>(null) }
    var enrollmentReport by remember { mutableStateOf<AdminEnrollmentReport?>(null) }
    var inventoryReport by remember { mutableStateOf<AdminInventoryReport?>(null) }
    var cashReport by remember { mutableStateOf<AdminCashReconciliationReport?>(null) }
    var exchangeQuote by remember { mutableStateOf<AdminExchangeRateQuote?>(null) }
    var loading by remember { mutableStateOf(true) }
    var exporting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var exportMenu by remember { mutableStateOf(false) }

    fun currentFilters(): AdminReportFilters = AdminReportFilters(
        trainerId = trainerId,
        productId = if (kind == AdminReportKind.SALES) productId else null,
        planId = if (kind == AdminReportKind.ENROLLMENTS) planId else null,
        categoryId = if (kind == AdminReportKind.INVENTORY) categoryId else null,
        clientId = when {
            kind == AdminReportKind.SALES || kind == AdminReportKind.MEMBERSHIPS -> clientId
            kind == AdminReportKind.SESSIONS && subjectType == "CLIENT" -> clientId
            else -> null
        },
        staffId = if (kind == AdminReportKind.SESSIONS && subjectType == "STAFF") staffId else null,
        subjectType = if (kind == AdminReportKind.SESSIONS) subjectType else null,
        clientStatus = if (kind == AdminReportKind.SESSIONS) clientStatus else null,
        membership = if (kind == AdminReportKind.SESSIONS) membership else null,
        status = when (kind) {
            AdminReportKind.SALES -> saleStatus
            AdminReportKind.INVENTORY -> stockLevel
            else -> null
        },
        method = if (kind == AdminReportKind.SALES) saleMethod else null,
        from = if (kind == AdminReportKind.INVENTORY) null else fromIso,
        to = when (kind) {
            AdminReportKind.CASH_RECONCILIATION -> fromIso
            AdminReportKind.INVENTORY -> null
            else -> toIso
        },
    )

    /** Rango por defecto de cada reporte: el día de hoy. */
    fun resetDateRangeToToday() {
        val today = todayIso()
        fromIso = today
        toIso = today
        fromInput = displayDate(today)
        toInput = displayDate(today)
    }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                if (trainers.isEmpty()) {
                    trainers = runCatching { AdminRepo.api.listTrainers() }.getOrDefault(emptyList())
                }
                if ((kind == AdminReportKind.SALES || kind == AdminReportKind.MEMBERSHIPS) && clients.isEmpty()) {
                    clients = runCatching { AdminRepo.api.listClients() }.getOrDefault(emptyList())
                }
                if (kind == AdminReportKind.SESSIONS) {
                    if (clients.isEmpty()) {
                        clients = runCatching { AdminRepo.api.listClients() }.getOrDefault(emptyList())
                    }
                }
                if (kind == AdminReportKind.SALES && products.isEmpty()) {
                    products = runCatching { AdminRepo.api.listProducts() }.getOrDefault(emptyList())
                }
                if ((kind == AdminReportKind.ENROLLMENTS || kind == AdminReportKind.SESSIONS) && plans.isEmpty()) {
                    plans = runCatching { AdminRepo.api.listMembershipPlans() }.getOrDefault(emptyList())
                }
                if (kind == AdminReportKind.INVENTORY && categories.isEmpty()) {
                    categories = runCatching { AdminRepo.api.listProductCategories() }.getOrDefault(emptyList())
                }
                when (kind) {
                    AdminReportKind.CASH_RECONCILIATION -> {
                        // No limpiar huérfanos al abrir el cuadre: puede borrar cobros
                        // aún visibles en Órdenes y desalinear el reporte.
                        cashReport = AdminRepo.api.cashReconciliationReport(currentFilters())
                        // Con "Usar tasa del BCV en divisas" apagado no se pide la tasa: los montos quedan como se registraron.
                        val calcOn = runCatching { AdminRepo.api.getSettingsModules().exchangeRatesEnabled }.getOrDefault(false)
                        exchangeQuote = if (calcOn) runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull() else null
                        sessionReport = null
                        membershipReport = null
                        salesReport = null
                        enrollmentReport = null
                        inventoryReport = null
                    }
                    AdminReportKind.SESSIONS -> {
                        sessionReport = AdminRepo.api.sessionReport(currentFilters())
                        membershipReport = null
                        salesReport = null
                        cashReport = null
                        enrollmentReport = null
                        inventoryReport = null
                    }
                    AdminReportKind.MEMBERSHIPS -> {
                        membershipReport = AdminRepo.api.membershipReport(currentFilters())
                        sessionReport = null
                        salesReport = null
                        cashReport = null
                        enrollmentReport = null
                        inventoryReport = null
                    }
                    AdminReportKind.ENROLLMENTS -> {
                        enrollmentReport = AdminRepo.api.enrollmentReport(currentFilters())
                        sessionReport = null
                        membershipReport = null
                        salesReport = null
                        cashReport = null
                        inventoryReport = null
                    }
                    AdminReportKind.SALES -> {
                        salesReport = AdminRepo.api.salesReport(currentFilters())
                        sessionReport = null
                        membershipReport = null
                        cashReport = null
                        enrollmentReport = null
                        inventoryReport = null
                    }
                    AdminReportKind.INVENTORY -> {
                        inventoryReport = AdminRepo.api.inventoryReport(currentFilters())
                        sessionReport = null
                        membershipReport = null
                        salesReport = null
                        cashReport = null
                        enrollmentReport = null
                    }
                }
            } catch (e: Exception) {
                error = e.userMessage("No se pudo cargar el reporte.")
            } finally {
                loading = false
            }
        }
    }

    fun resetFilters() {
        clientStatus = null
        membership = null
        trainerId = null
        productId = null
        categoryId = null
        stockLevel = null
        planId = null
        clientId = null
        staffId = null
        subjectType = null
        saleStatus = null
        saleMethod = null
        if (kind != AdminReportKind.INVENTORY) {
            resetDateRangeToToday()
        }
    }

    LaunchedEffect(user.permissionsKnown, allowed) {
        if (user.permissionsKnown && allowed.isNotEmpty() && kind !in allowed) {
            kind = allowed.first()
            resetDateRangeToToday()
        }
    }

    LaunchedEffect(kind, trainerId, clientId, staffId, subjectType, clientStatus, membership, saleStatus, saleMethod, productId, planId, categoryId, stockLevel, fromIso, toIso, user.permissionsKnown, allowed) {
        if (!user.permissionsKnown || kind !in allowed) return@LaunchedEffect
        reload()
    }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(horizontal = GymlySpace.lg, vertical = GymlySpace.md),
    ) {
        val compact = maxWidth < 700.dp
        if (!user.permissionsKnown) {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            return@BoxWithConstraints
        }
        if (allowed.isEmpty()) {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Text(
                    "No tienes permiso para ver reportes.",
                    color = AdminColors.TextMuted,
                    fontSize = 14.sp,
                )
            }
            return@BoxWithConstraints
        }
        // Scroll de página: listados crecen hasta 20 filas visibles (sin scroll interno aplastado).
        VerticalScrollColumn(Modifier.fillMaxSize()) {
            ReportsHeader()
            Spacer(Modifier.height(8.dp))
            ReportsTabs(kind, allowed) {
                kind = it
                // Cada pestaña vuelve a su rango por defecto (Inventario no tiene fechas).
                if (it != AdminReportKind.INVENTORY) {
                    resetDateRangeToToday()
                }
            }
            Spacer(Modifier.height(8.dp))
            when (kind) {
                AdminReportKind.CASH_RECONCILIATION -> {
                    val c = cashReport
                    val currency = c?.currency ?: GymlyCurrency.CODE
                    val (inBs, inUsd) = c?.cashInBsUsd(exchangeQuote) ?: (0L to 0L)
                    val (outBs, outUsd) = c?.cashOutBsUsd() ?: (0L to 0L)
                    ReportsKpiRow(
                        listOf(
                            Triple(
                                "Entradas",
                                // Cobrado bruto del día (sin restar vuelto).
                                formatCashBsUsd(inBs, inUsd, currency),
                                Color(0xFF2E7D32),
                            ),
                            Triple(
                                "Vuelto",
                                // Negativo: reduce el total de Bs en caja.
                                formatCashBsUsd(-outBs, -outUsd, currency),
                                Color(0xFFC62828),
                            ),
                            Triple(
                                "Neto",
                                // Entradas − Vuelto (lo que queda en caja).
                                formatCashBsUsd(inBs - outBs, inUsd - outUsd, currency),
                                AdminColors.Primary,
                            ),
                            Triple("Movimientos", (c?.rows?.size ?: 0).toString(), Color(0xFFF59E0B)),
                        ),
                        compact,
                    )
                }
                AdminReportKind.SESSIONS -> {
                    val s = sessionReport
                    ReportsKpiRow(
                        listOf(
                            Triple("Visitas", (s?.sessions ?: 0).toString(), AdminColors.Primary),
                            Triple("Clientes", (s?.clients ?: 0).toString(), Color(0xFF2E7D32)),
                            Triple("Entrenadores", (s?.staff ?: 0).toString(), Color(0xFF7C3AED)),
                            Triple("Por acceso", (s?.accessVisits ?: 0).toString(), Color(0xFFF59E0B)),
                        ),
                        compact,
                    )
                }
                AdminReportKind.MEMBERSHIPS -> {
                    val m = membershipReport
                    ReportsKpiRow(
                        listOf(
                            Triple("Membresías", (m?.memberships ?: 0).toString(), AdminColors.Primary),
                            Triple("Activas", (m?.active ?: 0).toString(), Color(0xFF2E7D32)),
                            Triple(
                                "Ingresos",
                                formatMoney((m?.incomeCents ?: 0L).toInt(), m?.currency ?: GymlyCurrency.CODE),
                                Color(0xFF7C3AED),
                            ),
                            Triple("Entrenadores", (m?.trainers ?: 0).toString(), Color(0xFFF59E0B)),
                        ),
                        compact,
                    )
                }
                AdminReportKind.ENROLLMENTS -> {
                    val e = enrollmentReport
                    ReportsKpiRow(
                        listOf(
                            Triple("Inscripciones", (e?.enrollments ?: 0).toString(), AdminColors.Primary),
                            Triple("Clientes", (e?.clients ?: 0).toString(), Color(0xFF2E7D32)),
                            Triple("Entrenadores", (e?.trainers ?: 0).toString(), Color(0xFF7C3AED)),
                            Triple("Planes", (e?.plans ?: 0).toString(), Color(0xFFF59E0B)),
                        ),
                        compact,
                    )
                }
                AdminReportKind.SALES -> {
                    val s = salesReport
                    ReportsKpiRow(
                        listOf(
                            Triple("Ventas", (s?.sales ?: 0).toString(), AdminColors.Primary),
                            Triple("Pagadas", (s?.paid ?: 0).toString(), Color(0xFF2E7D32)),
                            Triple(
                                "Ingresos",
                                formatMoney((s?.incomeCents ?: 0L).toInt(), s?.currency ?: GymlyCurrency.CODE),
                                Color(0xFF7C3AED),
                            ),
                            Triple(
                                "Vuelto",
                                formatMoney((s?.changeGivenCents ?: 0L).toInt(), s?.currency ?: GymlyCurrency.CODE),
                                Color(0xFFC62828),
                            ),
                        ),
                        compact,
                    )
                }
                AdminReportKind.INVENTORY -> {
                    val i = inventoryReport
                    ReportsKpiRow(
                        listOf(
                            Triple("Productos", (i?.products ?: 0).toString(), AdminColors.Primary),
                            Triple("Unidades", (i?.units ?: 0).toString(), Color(0xFF2E7D32)),
                            Triple("Poco stock", (i?.low ?: 0).toString(), Color(0xFFF59E0B)),
                            Triple("Agotados", (i?.out ?: 0).toString(), Color(0xFFC62828)),
                        ),
                        compact,
                    )
                }
            }
            Spacer(Modifier.height(8.dp))
            ReportsFiltersCard(
                compact = compact,
                kind = kind,
                trainers = trainers,
                clients = clients,
                products = products,
                categories = categories,
                plans = plans,
                trainerId = trainerId,
                productId = productId,
                categoryId = categoryId,
                stockLevel = stockLevel,
                planId = planId,
                clientId = clientId,
                staffId = staffId,
                subjectType = subjectType,
                clientStatus = clientStatus,
                membership = membership,
                saleStatus = saleStatus,
                saleMethod = saleMethod,
                fromInput = fromInput,
                toInput = toInput,
                onClientStatus = { clientStatus = it },
                onMembership = { membership = it },
                onTrainer = { trainerId = it },
                onProduct = { productId = it },
                onCategory = { categoryId = it },
                onStockLevel = { stockLevel = it },
                onPlan = { planId = it },
                onClient = { clientId = it },
                onStaff = { staffId = it },
                onSubjectType = {
                    subjectType = it
                    clientId = null
                    staffId = null
                },
                onSaleStatus = { saleStatus = it },
                onSaleMethod = { saleMethod = it },
                onFromInput = { fromInput = it },
                onToInput = { toInput = it },
                onApplyDates = {
                    if (kind == AdminReportKind.CASH_RECONCILIATION) {
                        val day = parseLooseDate(fromInput)
                        if (day == null) {
                            error = "Usa una fecha válida (DD/MM/AAAA o AAAA-MM-DD)."
                        } else {
                            fromIso = day
                            toIso = day
                            fromInput = displayDate(day)
                            toInput = displayDate(day)
                        }
                    } else {
                        val f = parseLooseDate(fromInput)
                        val t = parseLooseDate(toInput)
                        if (f == null || t == null) {
                            error = "Usa fechas válidas (DD/MM/AAAA o AAAA-MM-DD)."
                        } else {
                            fromIso = f
                            toIso = t
                            fromInput = displayDate(f)
                            toInput = displayDate(t)
                        }
                    }
                },
                onReset = { resetFilters() },
            )
            Spacer(Modifier.height(8.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 6.dp))
            }
            ReportsTableCard(
                kind = kind,
                loading = loading,
                exporting = exporting,
                compact = compact,
                sessionReport = sessionReport,
                membershipReport = membershipReport,
                salesReport = salesReport,
                enrollmentReport = enrollmentReport,
                inventoryReport = inventoryReport,
                cashReport = cashReport,
                exchangeQuote = exchangeQuote,
                exportMenu = exportMenu,
                onExportMenu = { exportMenu = it },
                onExport = { format ->
                    exportMenu = false
                    scope.launch {
                        exporting = true
                        error = null
                        try {
                            val file = AdminRepo.api.exportReport(kind, format, currentFilters())
                            downloadBytes(file.fileName, file.mimeType, file.bytes)
                        } catch (e: Exception) {
                            error = e.userMessage("No se pudo exportar el reporte.")
                        } finally {
                            exporting = false
                        }
                    }
                },
            )
        }
    }
}

@Composable
private fun ReportsHeader() {
    Column(Modifier.fillMaxWidth()) {
        Text("Reportes", fontSize = 22.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
        Text(
            "Filtros, detalle paginado y exportación PDF/Excel.",
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
            modifier = Modifier.padding(top = 2.dp),
        )
    }
}

@Composable
private fun ReportsTabs(
    kind: AdminReportKind,
    allowed: List<AdminReportKind>,
    onSelect: (AdminReportKind) -> Unit,
) {
    val items = listOf(
        AdminReportKind.CASH_RECONCILIATION to "Cuadre de caja",
        AdminReportKind.SESSIONS to "Asistencias",
        AdminReportKind.MEMBERSHIPS to "Membresías",
        AdminReportKind.ENROLLMENTS to "Inscritos",
        AdminReportKind.SALES to "Ventas",
        AdminReportKind.INVENTORY to "Inventario",
    ).filter { (value, _) -> value in allowed }
    // Celular: pestañas en filas de dos para verlas todas; escritorio: una sola fila.
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val narrow = maxWidth < 600.dp
        val perRow = if (narrow) 2 else items.size.coerceAtLeast(1)
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            items.chunked(perRow).forEach { rowItems ->
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    rowItems.forEach { (value, label) ->
                        val selected = kind == value
                        Text(
                            label,
                            textAlign = TextAlign.Center,
                            modifier = Modifier
                                .then(if (narrow) Modifier.weight(1f) else Modifier)
                                .clip(RoundedCornerShape(10.dp))
                                .background(if (selected) AdminColors.Primary else AdminColors.SurfaceContainer)
                                .border(
                                    width = if (selected) 0.dp else 1.dp,
                                    color = AdminColors.Outline.copy(alpha = 0.6f),
                                    shape = RoundedCornerShape(10.dp),
                                )
                                .clickable { onSelect(value) }
                                .padding(horizontal = 16.dp, vertical = 10.dp),
                            color = if (selected) AdminColors.OnBrand else AdminColors.Text,
                            fontWeight = FontWeight.SemiBold,
                            fontSize = 13.sp,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ReportsKpiRow(items: List<Triple<String, String, Color>>, compact: Boolean) {
    if (compact) {
        Row(
            Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            items.forEach { (label, value, accent) ->
                ReportsKpiCard(label, value, accent, Modifier.width(150.dp))
            }
        }
    } else {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            items.forEach { (label, value, accent) ->
                ReportsKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun ReportsKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    val lines = value
        .split('\n', '·')
        .map { it.trim() }
        .filter { it.isNotEmpty() }
    val dual = lines.size > 1
    Column(
        modifier
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(horizontal = 12.dp, vertical = 8.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(2.dp))
        if (dual) {
            lines.forEach { line ->
                Text(
                    line,
                    fontWeight = FontWeight.Bold,
                    fontSize = 14.sp,
                    color = accent,
                    maxLines = 1,
                )
            }
        } else {
            Text(
                value,
                fontWeight = FontWeight.Bold,
                fontSize = 18.sp,
                color = accent,
                maxLines = 1,
            )
        }
    }
}

@Composable
private fun ReportsFiltersCard(
    compact: Boolean,
    kind: AdminReportKind,
    trainers: List<AdminTrainer>,
    clients: List<AdminClient>,
    products: List<AdminProduct>,
    categories: List<AdminProductCategoryItem>,
    plans: List<AdminMembershipPlanItem>,
    trainerId: String?,
    productId: String?,
    categoryId: String?,
    stockLevel: String?,
    planId: String?,
    clientId: String?,
    staffId: String?,
    subjectType: String?,
    saleStatus: String?,
    saleMethod: String?,
    fromInput: String,
    toInput: String,
    onTrainer: (String?) -> Unit,
    onProduct: (String?) -> Unit,
    onCategory: (String?) -> Unit,
    onStockLevel: (String?) -> Unit,
    onPlan: (String?) -> Unit,
    onClient: (String?) -> Unit,
    onStaff: (String?) -> Unit,
    clientStatus: String?,
    membership: String?,
    onClientStatus: (String?) -> Unit,
    onMembership: (String?) -> Unit,
    onSubjectType: (String?) -> Unit,
    onSaleStatus: (String?) -> Unit,
    onSaleMethod: (String?) -> Unit,
    onFromInput: (String) -> Unit,
    onToInput: (String) -> Unit,
    onApplyDates: () -> Unit,
    onReset: () -> Unit,
) {
    var trainerMenu by remember { mutableStateOf(false) }
    var clientMenu by remember { mutableStateOf(false) }
    var staffMenu by remember { mutableStateOf(false) }
    var subjectTypeMenu by remember { mutableStateOf(false) }
    var statusMenu by remember { mutableStateOf(false) }
    var methodMenu by remember { mutableStateOf(false) }
    var productMenu by remember { mutableStateOf(false) }
    var categoryMenu by remember { mutableStateOf(false) }
    var stockMenu by remember { mutableStateOf(false) }
    var planMenu by remember { mutableStateOf(false) }
    val trainerLabel = trainers.firstOrNull { it.id == trainerId }?.displayName ?: "Todos los entrenadores"
    val productLabel = products.firstOrNull { it.id == productId }?.name ?: "Todos los productos"
    val planLabel = plans.firstOrNull { it.id == planId }?.name ?: "Todos los planes"
    var clientStatusMenu by remember { mutableStateOf(false) }
    var membershipMenu by remember { mutableStateOf(false) }
    val clientStatusLabel = when (clientStatus) {
        "ACTIVE" -> "Activos"
        "INACTIVE" -> "Inactivos"
        else -> "Todos los estados"
    }
    val membershipLabel = when (membership) {
        null, "" -> "Todas las membresías"
        "NONE" -> "Sin membresía"
        else -> plans.firstOrNull { it.id == membership }?.name ?: "Todas las membresías"
    }
    val clientLabel = clients.firstOrNull { it.id == clientId }?.fullName ?: "Todos los clientes"
    val trainerPersonaLabel = trainers.firstOrNull { it.id == staffId }?.displayName ?: "Todos los entrenadores"
    val subjectTypeLabel = when (subjectType) {
        "CLIENT" -> "Clientes"
        "STAFF" -> "Entrenadores"
        else -> "Todos"
    }
    val statusLabel = saleStatus?.let { AdminStoreSaleStatus.fromApi(it).labelEs() } ?: "Todos los estados"
    val methodLabel = saleMethod?.let { AdminStoreSaleMethod.fromApi(it).labelEs() } ?: "Todos los métodos"

    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(horizontal = 12.dp, vertical = 10.dp),
    ) {
        Text("Filtros", fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
        Spacer(Modifier.height(8.dp))
        if (kind == AdminReportKind.CASH_RECONCILIATION) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalAlignment = Alignment.Bottom,
            ) {
                Box(Modifier.weight(1f)) { DateField("Día", fromInput, onFromInput) }
                Button(
                    onClick = onApplyDates,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) { Text("Aplicar") }
                TextButton(onClick = onReset) {
                    Text("Restablecer", color = AdminColors.Primary)
                }
            }
            return@Column
        }
        if (kind == AdminReportKind.INVENTORY) {
            val categoryLabel = categories.firstOrNull { it.id == categoryId }?.name ?: "Todas las categorías"
            val stockLabel = stockLevel?.let { AdminProductStockLevel.fromApi(it).labelEs() } ?: "Todo el inventario"
            if (compact) {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                FilterDropdown("Categoría", categoryLabel, categoryMenu, { categoryMenu = it }) {
                    DropdownMenuItem(text = { Text("Todas las categorías") }, onClick = {
                        onCategory(null); categoryMenu = false
                    })
                    categories.forEach { c ->
                        DropdownMenuItem(text = { Text(c.name) }, onClick = {
                            onCategory(c.id); categoryMenu = false
                        })
                    }
                }
                FilterDropdown("Inventario", stockLabel, stockMenu, { stockMenu = it }) {
                    DropdownMenuItem(text = { Text("Todo el inventario") }, onClick = {
                        onStockLevel(null); stockMenu = false
                    })
                    AdminProductStockLevel.entries.forEach { level ->
                        DropdownMenuItem(text = { Text(level.labelEs()) }, onClick = {
                            onStockLevel(level.name); stockMenu = false
                        })
                    }
                }
                TextButton(onClick = onReset) {
                    Text("Restablecer", color = AdminColors.Primary)
                }
                }
            } else {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.Bottom,
                ) {
                    Box(Modifier.weight(1f)) {
                        FilterDropdown("Categoría", categoryLabel, categoryMenu, { categoryMenu = it }) {
                            DropdownMenuItem(text = { Text("Todas las categorías") }, onClick = {
                                onCategory(null); categoryMenu = false
                            })
                            categories.forEach { c ->
                                DropdownMenuItem(text = { Text(c.name) }, onClick = {
                                    onCategory(c.id); categoryMenu = false
                                })
                            }
                        }
                    }
                    Box(Modifier.weight(1f)) {
                        FilterDropdown("Inventario", stockLabel, stockMenu, { stockMenu = it }) {
                            DropdownMenuItem(text = { Text("Todo el inventario") }, onClick = {
                                onStockLevel(null); stockMenu = false
                            })
                            AdminProductStockLevel.entries.forEach { level ->
                                DropdownMenuItem(text = { Text(level.labelEs()) }, onClick = {
                                    onStockLevel(level.name); stockMenu = false
                                })
                            }
                        }
                    }
                    TextButton(onClick = onReset) {
                        Text("Restablecer", color = AdminColors.Primary)
                    }
                }
            }
            Text(
                "Inventario actual y datos de cada producto. Poco stock es cuando la cantidad llega al mínimo.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
            return@Column
        }
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (compact) {
                if (kind == AdminReportKind.SESSIONS) {
                    FilterDropdown("Tipo", subjectTypeLabel, subjectTypeMenu, { subjectTypeMenu = it }) {
                        DropdownMenuItem(text = { Text("Todos") }, onClick = {
                            onSubjectType(null); subjectTypeMenu = false
                        })
                        DropdownMenuItem(text = { Text("Clientes") }, onClick = {
                            onSubjectType("CLIENT"); subjectTypeMenu = false
                        })
                        DropdownMenuItem(text = { Text("Entrenadores") }, onClick = {
                            onSubjectType("STAFF"); subjectTypeMenu = false
                        })
                    }
                    if (subjectType != "STAFF") FilterDropdown("Entrenador", trainerLabel, trainerMenu, { trainerMenu = it }) {
                        DropdownMenuItem(text = { Text("Todos los entrenadores") }, onClick = {
                            onTrainer(null); trainerMenu = false
                        })
                        trainers.forEach { t ->
                            DropdownMenuItem(text = { Text(t.displayName) }, onClick = {
                                onTrainer(t.id); trainerMenu = false
                            })
                        }
                    }
                    if (subjectType == "CLIENT") {
                        FilterDropdown("Persona", clientLabel, clientMenu, { clientMenu = it }) {
                            DropdownMenuItem(text = { Text("Todos los clientes") }, onClick = {
                                onClient(null); clientMenu = false
                            })
                            clients.take(80).forEach { c ->
                                DropdownMenuItem(text = { Text(c.fullName) }, onClick = {
                                    onClient(c.id); clientMenu = false
                                })
                            }
                        }
                    }
                    if (subjectType == "STAFF") {
                        FilterDropdown("Persona", trainerPersonaLabel, staffMenu, { staffMenu = it }) {
                            DropdownMenuItem(text = { Text("Todos los entrenadores") }, onClick = {
                                onStaff(null); staffMenu = false
                            })
                            trainers.forEach { t ->
                                DropdownMenuItem(text = { Text(t.displayName) }, onClick = {
                                    onStaff(t.id); staffMenu = false
                                })
                            }
                        }
                    }
                } else {
                    if (subjectType != "STAFF") FilterDropdown("Entrenador", trainerLabel, trainerMenu, { trainerMenu = it }) {
                        DropdownMenuItem(text = { Text("Todos los entrenadores") }, onClick = {
                            onTrainer(null); trainerMenu = false
                        })
                        trainers.forEach { t ->
                            DropdownMenuItem(text = { Text(t.displayName) }, onClick = {
                                onTrainer(t.id); trainerMenu = false
                            })
                        }
                    }
                    if (subjectType != "STAFF") {
                        FilterDropdown("Estado", clientStatusLabel, clientStatusMenu, { clientStatusMenu = it }) {
                            DropdownMenuItem(text = { Text("Todos los estados") }, onClick = {
                                onClientStatus(null); clientStatusMenu = false
                            })
                            DropdownMenuItem(text = { Text("Activos") }, onClick = {
                                onClientStatus("ACTIVE"); clientStatusMenu = false
                            })
                            DropdownMenuItem(text = { Text("Inactivos") }, onClick = {
                                onClientStatus("INACTIVE"); clientStatusMenu = false
                            })
                        }
                        FilterDropdown("Membresía", membershipLabel, membershipMenu, { membershipMenu = it }) {
                            DropdownMenuItem(text = { Text("Todas las membresías") }, onClick = {
                                onMembership(null); membershipMenu = false
                            })
                            DropdownMenuItem(text = { Text("Sin membresía") }, onClick = {
                                onMembership("NONE"); membershipMenu = false
                            })
                            plans.forEach { p ->
                                DropdownMenuItem(text = { Text(p.name) }, onClick = {
                                    onMembership(p.id); membershipMenu = false
                                })
                            }
                        }
                    }
                    if (kind == AdminReportKind.ENROLLMENTS) {
                        FilterDropdown("Plan", planLabel, planMenu, { planMenu = it }) {
                            DropdownMenuItem(text = { Text("Todos los planes") }, onClick = {
                                onPlan(null); planMenu = false
                            })
                            plans.forEach { p ->
                                DropdownMenuItem(text = { Text(p.name) }, onClick = {
                                    onPlan(p.id); planMenu = false
                                })
                            }
                        }
                    }
                    if (kind == AdminReportKind.SALES || kind == AdminReportKind.MEMBERSHIPS) {
                        FilterDropdown("Cliente", clientLabel, clientMenu, { clientMenu = it }) {
                            DropdownMenuItem(text = { Text("Todos los clientes") }, onClick = {
                                onClient(null); clientMenu = false
                            })
                            clients.take(80).forEach { c ->
                                DropdownMenuItem(text = { Text(c.fullName) }, onClick = {
                                    onClient(c.id); clientMenu = false
                                })
                            }
                        }
                    }
                    if (kind == AdminReportKind.SALES) {
                        FilterDropdown("Estado", statusLabel, statusMenu, { statusMenu = it }) {
                            DropdownMenuItem(text = { Text("Todos los estados") }, onClick = {
                                onSaleStatus(null); statusMenu = false
                            })
                            AdminStoreSaleStatus.entries.forEach { s ->
                                DropdownMenuItem(text = { Text(s.labelEs()) }, onClick = {
                                    onSaleStatus(s.name); statusMenu = false
                                })
                            }
                        }
                        FilterDropdown("Método de pago", methodLabel, methodMenu, { methodMenu = it }) {
                            DropdownMenuItem(text = { Text("Todos los métodos") }, onClick = {
                                onSaleMethod(null); methodMenu = false
                            })
                            AdminStoreSaleMethod.entries.forEach { m ->
                                DropdownMenuItem(text = { Text(m.labelEs()) }, onClick = {
                                    onSaleMethod(m.name); methodMenu = false
                                })
                            }
                        }
                        FilterDropdown("Producto", productLabel, productMenu, { productMenu = it }) {
                            DropdownMenuItem(text = { Text("Todos los productos") }, onClick = {
                                onProduct(null); productMenu = false
                            })
                            products.forEach { p ->
                                DropdownMenuItem(text = { Text(p.name) }, onClick = {
                                    onProduct(p.id); productMenu = false
                                })
                            }
                        }
                    }
                }
                DateField("Desde", fromInput, onFromInput)
                DateField("Hasta", toInput, onToInput)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    Button(
                        onClick = onApplyDates,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) { Text("Aplicar") }
                    TextButton(onClick = onReset) {
                        Text("Restablecer", color = AdminColors.Primary)
                    }
                }
            } else {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.Bottom,
                ) {
                    if (kind == AdminReportKind.SESSIONS) {
                        Box(Modifier.weight(1f)) {
                            FilterDropdown("Tipo", subjectTypeLabel, subjectTypeMenu, { subjectTypeMenu = it }) {
                                DropdownMenuItem(text = { Text("Todos") }, onClick = {
                                    onSubjectType(null); subjectTypeMenu = false
                                })
                                DropdownMenuItem(text = { Text("Clientes") }, onClick = {
                                    onSubjectType("CLIENT"); subjectTypeMenu = false
                                })
                                DropdownMenuItem(text = { Text("Entrenadores") }, onClick = {
                                    onSubjectType("STAFF"); subjectTypeMenu = false
                                })
                            }
                        }
                        if (subjectType != "STAFF") Box(Modifier.weight(1f)) {
                            FilterDropdown("Entrenador", trainerLabel, trainerMenu, { trainerMenu = it }) {
                                DropdownMenuItem(text = { Text("Todos los entrenadores") }, onClick = {
                                    onTrainer(null); trainerMenu = false
                                })
                                trainers.forEach { t ->
                                    DropdownMenuItem(text = { Text(t.displayName) }, onClick = {
                                        onTrainer(t.id); trainerMenu = false
                                    })
                                }
                            }
                        }
                        if (subjectType == "CLIENT") {
                            Box(Modifier.weight(1f)) {
                                FilterDropdown("Persona", clientLabel, clientMenu, { clientMenu = it }) {
                                    DropdownMenuItem(text = { Text("Todos los clientes") }, onClick = {
                                        onClient(null); clientMenu = false
                                    })
                                    clients.take(80).forEach { c ->
                                        DropdownMenuItem(text = { Text(c.fullName) }, onClick = {
                                            onClient(c.id); clientMenu = false
                                        })
                                    }
                                }
                            }
                        }
                        if (subjectType == "STAFF") {
                            Box(Modifier.weight(1f)) {
                                FilterDropdown("Persona", trainerPersonaLabel, staffMenu, { staffMenu = it }) {
                                    DropdownMenuItem(text = { Text("Todos los entrenadores") }, onClick = {
                                        onStaff(null); staffMenu = false
                                    })
                                    trainers.forEach { t ->
                                        DropdownMenuItem(text = { Text(t.displayName) }, onClick = {
                                            onStaff(t.id); staffMenu = false
                                        })
                                    }
                                }
                            }
                        }
                    } else {
                        if (subjectType != "STAFF") Box(Modifier.weight(1f)) {
                            FilterDropdown("Entrenador", trainerLabel, trainerMenu, { trainerMenu = it }) {
                                DropdownMenuItem(text = { Text("Todos los entrenadores") }, onClick = {
                                    onTrainer(null); trainerMenu = false
                                })
                                trainers.forEach { t ->
                                    DropdownMenuItem(text = { Text(t.displayName) }, onClick = {
                                        onTrainer(t.id); trainerMenu = false
                                    })
                                }
                            }
                        }
                        if (kind == AdminReportKind.SESSIONS && subjectType != "STAFF") {
                            Box(Modifier.weight(1f)) {
                                FilterDropdown("Estado", clientStatusLabel, clientStatusMenu, { clientStatusMenu = it }) {
                                    DropdownMenuItem(text = { Text("Todos los estados") }, onClick = {
                                        onClientStatus(null); clientStatusMenu = false
                                    })
                                    DropdownMenuItem(text = { Text("Activos") }, onClick = {
                                        onClientStatus("ACTIVE"); clientStatusMenu = false
                                    })
                                    DropdownMenuItem(text = { Text("Inactivos") }, onClick = {
                                        onClientStatus("INACTIVE"); clientStatusMenu = false
                                    })
                                }
                            }
                            Box(Modifier.weight(1f)) {
                                FilterDropdown("Membresía", membershipLabel, membershipMenu, { membershipMenu = it }) {
                                    DropdownMenuItem(text = { Text("Todas las membresías") }, onClick = {
                                        onMembership(null); membershipMenu = false
                                    })
                                    DropdownMenuItem(text = { Text("Sin membresía") }, onClick = {
                                        onMembership("NONE"); membershipMenu = false
                                    })
                                    plans.forEach { p ->
                                        DropdownMenuItem(text = { Text(p.name) }, onClick = {
                                            onMembership(p.id); membershipMenu = false
                                        })
                                    }
                                }
                            }
                        }
                        if (kind == AdminReportKind.ENROLLMENTS) {
                            Box(Modifier.weight(1f)) {
                                FilterDropdown("Plan", planLabel, planMenu, { planMenu = it }) {
                                    DropdownMenuItem(text = { Text("Todos los planes") }, onClick = {
                                        onPlan(null); planMenu = false
                                    })
                                    plans.forEach { p ->
                                        DropdownMenuItem(text = { Text(p.name) }, onClick = {
                                            onPlan(p.id); planMenu = false
                                        })
                                    }
                                }
                            }
                        }
                        if (kind == AdminReportKind.SALES || kind == AdminReportKind.MEMBERSHIPS) {
                            Box(Modifier.weight(1f)) {
                                FilterDropdown("Cliente", clientLabel, clientMenu, { clientMenu = it }) {
                                    DropdownMenuItem(text = { Text("Todos los clientes") }, onClick = {
                                        onClient(null); clientMenu = false
                                    })
                                    clients.take(80).forEach { c ->
                                        DropdownMenuItem(text = { Text(c.fullName) }, onClick = {
                                            onClient(c.id); clientMenu = false
                                        })
                                    }
                                }
                            }
                        }
                    }
                    Box(Modifier.weight(1f)) { DateField("Desde", fromInput, onFromInput) }
                    Box(Modifier.weight(1f)) { DateField("Hasta", toInput, onToInput) }
                    Button(
                        onClick = onApplyDates,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) { Text("Aplicar") }
                    TextButton(onClick = onReset) {
                        Text("Restablecer", color = AdminColors.Primary)
                    }
                }
                if (kind == AdminReportKind.SALES) {
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalAlignment = Alignment.Bottom,
                    ) {
                        Box(Modifier.weight(1f)) {
                            FilterDropdown("Estado", statusLabel, statusMenu, { statusMenu = it }) {
                                DropdownMenuItem(text = { Text("Todos los estados") }, onClick = {
                                    onSaleStatus(null); statusMenu = false
                                })
                                AdminStoreSaleStatus.entries.forEach { s ->
                                    DropdownMenuItem(text = { Text(s.labelEs()) }, onClick = {
                                        onSaleStatus(s.name); statusMenu = false
                                    })
                                }
                            }
                        }
                        Box(Modifier.weight(1f)) {
                            FilterDropdown("Producto", productLabel, productMenu, { productMenu = it }) {
                                DropdownMenuItem(text = { Text("Todos los productos") }, onClick = {
                                    onProduct(null); productMenu = false
                                })
                                products.forEach { p ->
                                    DropdownMenuItem(text = { Text(p.name) }, onClick = {
                                        onProduct(p.id); productMenu = false
                                    })
                                }
                            }
                        }
                        Box(Modifier.weight(1f)) {
                            FilterDropdown("Método de pago", methodLabel, methodMenu, { methodMenu = it }) {
                                DropdownMenuItem(text = { Text("Todos los métodos") }, onClick = {
                                    onSaleMethod(null); methodMenu = false
                                })
                                AdminStoreSaleMethod.entries.forEach { m ->
                                    DropdownMenuItem(text = { Text(m.labelEs()) }, onClick = {
                                        onSaleMethod(m.name); methodMenu = false
                                    })
                                }
                            }
                        }
                    }
                    Text(
                        "Cada fila es un producto vendido en el rango de fechas. Stock es el inventario actual.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(top = 4.dp),
                    )
                }
            }
            if (kind == AdminReportKind.SESSIONS) {
                Text(
                    "Asistentes por entrenador y fechas. El entrenador es el de la membresía activa del cliente.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
            }
            if (kind == AdminReportKind.ENROLLMENTS) {
                Text(
                    "Personas inscritas al registrarse en un plan y elegir entrenador, o al pagar el plan, entre las fechas.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
            }
        }
    }
}

@Composable
private fun FilterDropdown(
    label: String,
    value: String,
    expanded: Boolean,
    onExpanded: (Boolean) -> Unit,
    menu: @Composable () -> Unit,
) {
    Column {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(4.dp))
        Box {
            OutlinedButton(
                onClick = { onExpanded(true) },
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(10.dp),
            ) {
                Text(value, color = AdminColors.Text, modifier = Modifier.fillMaxWidth())
            }
            DropdownMenu(expanded = expanded, onDismissRequest = { onExpanded(false) }) { menu() }
        }
    }
}

@Composable
private fun DateField(label: String, value: String, onChange: (String) -> Unit) {
    AdminDateField(
        label = label,
        value = value,
        onValueChange = onChange,
        outputFormat = AdminDateOutputFormat.DISPLAY,
        placeholder = "DD-MM-AAAA",
        useFloatingLabel = true,
    )
}

@Composable
private fun ReportsTotalsFooter(
    totals: List<AdminPaymentMethodTotal>,
    currency: String,
    compact: Boolean,
) {
    if (totals.isEmpty()) return
    Spacer(Modifier.height(12.dp))
    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.6f))
    Spacer(Modifier.height(8.dp))
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            totals.forEach { t ->
                val display = methodTotalDisplay(t, currency)
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Text(
                        if (t.method == "TOTAL") "Total" else "${t.methodLabel} (${t.count})",
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 13.sp,
                    )
                    Text(
                        formatMoney(display.cents.toInt(), display.currency),
                        fontWeight = FontWeight.Bold,
                        fontSize = 13.sp,
                        color = AdminColors.Primary,
                    )
                }
                methodTotalSubtitle(t, currency)?.let { detail ->
                    Text(detail, color = AdminColors.TextMuted, fontSize = 12.sp)
                }
            }
        }
    } else {
        Column(Modifier.fillMaxWidth()) {
            totals.forEach { t ->
                val display = methodTotalDisplay(t, currency)
                Row(
                    Modifier
                        .fillMaxWidth()
                        .padding(vertical = 8.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(Modifier.weight(1f)) {
                        Text(
                            if (t.method == "TOTAL") "Total" else "Total ${t.methodLabel} (${t.count})",
                            fontWeight = FontWeight.Bold,
                            fontSize = 13.sp,
                        )
                        methodTotalSubtitle(t, currency)?.let { detail ->
                            Text(detail, color = AdminColors.TextMuted, fontSize = 12.sp)
                        }
                    }
                    Text(
                        formatMoney(display.cents.toInt(), display.currency),
                        fontWeight = FontWeight.Bold,
                        fontSize = 13.sp,
                        color = AdminColors.Primary,
                    )
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.25f))
            }
        }
    }
}

/** Divisas / USDT / Zelle: monto principal en USD; el resto de métodos en Bs. */
private fun methodTotalDisplay(t: AdminPaymentMethodTotal, fallbackCurrency: String): CashDisplayAmount {
    val usd = t.foreignUsdCents
    if (usd != null && usd != 0L && isUsdCashMethod(t.method)) {
        return CashDisplayAmount(usd, "USD")
    }
    return CashDisplayAmount(t.totalCents, fallbackCurrency)
}

private fun methodTotalSubtitle(t: AdminPaymentMethodTotal, reportCurrency: String): String? {
    if (!isUsdCashMethod(t.method)) return null
    val parts = mutableListOf<String>()
    if (t.foreignUsdCents != null && t.foreignUsdCents != 0L && t.totalCents != 0L) {
        parts += "equiv. ${formatMoney(t.totalCents.toInt(), reportCurrency)}"
    }
    t.foreignEurCents?.takeIf { it > 0 }?.let { parts += formatMoney(it.toInt(), "EUR") }
    return parts.joinToString(" · ").ifBlank { null }
}

private fun membershipRowMoney(r: AdminMembershipReportRow): String {
    val usd = r.foreignUsdCents
    if (usd != null && usd != 0L && isUsdCashMethod(r.method)) {
        return formatMoney(usd.toInt(), "USD")
    }
    return formatMoney(r.priceCents.toInt(), r.currency)
}

/** Pendiente de la venta: "Bs x · $ y", o "—" si no hay saldo. Solo viene en la primera fila de cada venta. */
private fun salesPendingText(r: AdminSalesReportRow): String {
    val parts = buildList {
        if (r.pendingBsCents > 0L) add(formatMoney(r.pendingBsCents.toInt(), r.currency))
        if (r.pendingUsdCents > 0L) add(formatMoney(r.pendingUsdCents.toInt(), "USD"))
    }
    return parts.joinToString(" · ").ifBlank { "—" }
}

/**
 * Descuento de una fila: "Bs 365,00", o "Bs 365,00 · $10,00" si se escribió en $ (el equivalente en Bs ya viene
 * descontado en el cobro). "—" si no hay descuento.
 */
private fun discountCellText(bsCents: Long, usdCents: Long, currency: String): String {
    val parts = buildList {
        if (bsCents > 0L) add(formatMoney(bsCents.toInt(), currency))
        if (usdCents > 0L) add(formatMoney(usdCents.toInt(), "USD"))
    }
    return parts.joinToString(" · ").ifBlank { "—" }
}

/** Cobrado de la membresía: lo pagado en Bs y/o en $. El precio del plan no es lo cobrado. */
private fun membershipPaidText(r: AdminMembershipReportRow): String {
    val parts = buildList {
        if (r.paidCents > 0L) add(formatMoney(r.paidCents.toInt(), r.currency))
        r.foreignUsdCents?.takeIf { it > 0L }?.let { add(formatMoney(it.toInt(), "USD")) }
    }
    return parts.joinToString(" · ").ifBlank { "—" }
}

/** Pendiente de la orden de membresía: "Bs x · $ y", o "—" si no hay saldo. */
private fun membershipPendingText(r: AdminMembershipReportRow): String {
    val parts = buildList {
        if (r.pendingBsCents > 0L) add(formatMoney(r.pendingBsCents.toInt(), r.currency))
        if (r.pendingUsdCents > 0L) add(formatMoney(r.pendingUsdCents.toInt(), "USD"))
    }
    return parts.joinToString(" · ").ifBlank { "—" }
}

private fun salesPaymentsText(r: AdminSalesReportRow): String = r.paymentsSummary.ifBlank { "—" }

/** Totales de las columnas de dinero del detalle de ventas: suman todas las filas del reporte, no solo la página. */
@Composable
private fun SalesColumnTotals(rows: List<AdminSalesReportRow>, currency: String) {
    if (rows.isEmpty()) return
    val showsUsd = { r: AdminSalesReportRow -> isUsdCashMethod(r.method) && (r.foreignUsdCents ?: 0L) != 0L }
    val lines = listOf(
        "Total" to salesMoneyPair(
            rows.filterNot(showsUsd).sumOf { it.totalCents },
            rows.filter(showsUsd).sumOf { it.foreignUsdCents ?: 0L },
            currency,
        ),
        "Descuento" to salesMoneyPair(rows.sumOf { it.discountCents }, rows.sumOf { it.discountUsdCents }, currency),
        "Cobrado" to salesMoneyPair(rows.sumOf { it.amountPaidCents }, 0L, currency),
        // El vuelto se resta de lo cobrado: se muestra en negativo.
        "Vuelto" to salesMoneyPair(-rows.sumOf { it.changeGivenCents }, 0L, currency),
        "Pendiente" to salesMoneyPair(rows.sumOf { it.pendingBsCents }, rows.sumOf { it.pendingUsdCents }, currency),
    )
    Column(Modifier.fillMaxWidth().padding(top = 6.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text("Totales por columna", fontWeight = FontWeight.Bold, fontSize = 13.sp)
        lines.forEach { (label, value) ->
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text(label, color = AdminColors.TextMuted, fontSize = 13.sp)
                Text(value, fontWeight = FontWeight.Bold, fontSize = 13.sp, color = AdminColors.Text)
            }
        }
    }
}

private fun salesMoneyPair(bs: Long, usd: Long, currency: String): String =
    listOfNotNull(
        bs.takeIf { it != 0L }?.let { formatMoney(it.toInt(), currency) },
        usd.takeIf { it != 0L }?.let { formatMoney(it.toInt(), "USD") },
    ).joinToString(" · ").ifBlank { "—" }

private fun salesRowMoney(r: AdminSalesReportRow): String {
    val usd = r.foreignUsdCents
    if (usd != null && usd != 0L && isUsdCashMethod(r.method)) {
        return formatMoney(usd.toInt(), "USD")
    }
    return formatMoney(r.totalCents.toInt(), r.currency)
}

@Composable
private fun ColumnScope.ReportsTableCard(
    kind: AdminReportKind,
    loading: Boolean,
    exporting: Boolean,
    compact: Boolean,
    sessionReport: AdminSessionReport?,
    membershipReport: AdminMembershipReport?,
    salesReport: AdminSalesReport?,
    enrollmentReport: AdminEnrollmentReport?,
    inventoryReport: AdminInventoryReport?,
    cashReport: AdminCashReconciliationReport?,
    exchangeQuote: AdminExchangeRateQuote?,
    exportMenu: Boolean,
    onExportMenu: (Boolean) -> Unit,
    onExport: (AdminReportExportFormat) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(12.dp),
    ) {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                when (kind) {
                    AdminReportKind.CASH_RECONCILIATION -> "Detalle del día"
                    AdminReportKind.SESSIONS -> "Detalle de asistencias"
                    AdminReportKind.MEMBERSHIPS -> "Detalle de membresías"
                    AdminReportKind.ENROLLMENTS -> "Detalle de inscritos"
                    AdminReportKind.SALES -> "Detalle de ventas"
                    AdminReportKind.INVENTORY -> "Detalle de inventario"
                },
                fontWeight = FontWeight.Bold,
                fontSize = 15.sp,
            )
            Box {
                OutlinedButton(
                    onClick = {
                        if (!exporting && !loading) {
                            onExportMenu(true)
                        }
                    },
                    enabled = !exporting && !loading,
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Icon(Icons.Outlined.FileDownload, null, tint = AdminColors.Text)
                    Spacer(Modifier.width(6.dp))
                    Text(if (exporting) "…" else "Exportar", color = AdminColors.Text)
                }
                DropdownMenu(
                    expanded = exportMenu,
                    onDismissRequest = { onExportMenu(false) },
                ) {
                    DropdownMenuItem(
                        text = { Text("PDF") },
                        onClick = {
                            onExportMenu(false)
                            onExport(AdminReportExportFormat.PDF)
                        },
                    )
                    DropdownMenuItem(
                        text = { Text("Excel (.xlsx)") },
                        onClick = {
                            onExportMenu(false)
                            onExport(AdminReportExportFormat.XLSX)
                        },
                    )
                }
            }
        }
        Spacer(Modifier.height(8.dp))
        when {
            loading -> Box(Modifier.fillMaxWidth().padding(24.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            kind == AdminReportKind.CASH_RECONCILIATION -> CashReconciliationReportDetail(
                report = cashReport,
                exchangeQuote = exchangeQuote,
                compact = compact,
                modifier = Modifier.fillMaxWidth(),
            )
            kind == AdminReportKind.SESSIONS -> SessionsReportDetail(
                report = sessionReport,
                compact = compact,
                modifier = Modifier.fillMaxWidth(),
            )
            kind == AdminReportKind.SALES -> SalesReportDetail(
                report = salesReport,
                compact = compact,
                modifier = Modifier.fillMaxWidth(),
            )
            kind == AdminReportKind.ENROLLMENTS -> EnrollmentsReportDetail(
                report = enrollmentReport,
                compact = compact,
                modifier = Modifier.fillMaxWidth(),
            )
            kind == AdminReportKind.INVENTORY -> InventoryReportDetail(
                report = inventoryReport,
                compact = compact,
                modifier = Modifier.fillMaxWidth(),
            )
            else -> MembershipsReportDetail(
                report = membershipReport,
                compact = compact,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@Composable
private fun ReportsDetailShell(
    modifier: Modifier = Modifier,
    footer: (@Composable () -> Unit)? = null,
    pageBar: @Composable () -> Unit,
    content: @Composable ColumnScope.() -> Unit,
) {
    Column(modifier.fillMaxWidth()) {
        // Altura natural en todas las pestañas: sin altura fija, ninguna fila se recorta y la paginación
        // queda a la misma distancia de la última fila. La página completa hace el scroll.
        Column(Modifier.fillMaxWidth()) {
            content()
        }
        Spacer(Modifier.height(REPORTS_PAGE_BAR_GAP))
        pageBar()
        footer?.invoke()
    }
}

/** Separación entre la última fila de cualquier reporte y la barra de paginación. */
private val REPORTS_PAGE_BAR_GAP = 8.dp

@Composable
private fun SessionsReportDetail(
    report: AdminSessionReport?,
    compact: Boolean,
    modifier: Modifier = Modifier,
) {
    val rows = report?.rows.orEmpty()
    val listPaging = rememberAdminListPaging(
        initialSortKey = "date",
        initialDir = AdminSortDir.Desc,
        resetKey = report,
    )
    val pageItems = remember(rows, listPaging.sort, listPaging.page) {
        rows.adminSortedPaged(listPaging.sort, listPaging.page, REPORTS_PAGE_SIZE) { key ->
            when (key) {
                "time" -> { r -> r.occurredAt }
                "type" -> { r -> r.subjectTypeLabel.lowercase() }
                "name" -> { r -> r.subjectName.lowercase() }
                "source" -> { r -> r.sourceLabel.lowercase() }
                "schedule" -> { r -> r.scheduleTitle.lowercase() }
                "trainer" -> { r -> r.trainerName.lowercase() }
                else -> { r -> r.date + r.occurredAt }
            }
        }
    }
    if (rows.isEmpty()) {
        Box(modifier) {
            Text("Sin datos para los filtros seleccionados.", color = AdminColors.TextMuted)
        }
        return
    }
    ReportsDetailShell(
        modifier = modifier,
        pageBar = {
            AdminListPaginationBar(
                totalItems = rows.size,
                page = listPaging.page,
                onPageChange = { listPaging.page = it },
                pageSize = REPORTS_PAGE_SIZE,
            )
        },
        footer = null,
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                pageItems.forEach { r ->
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .border(1.dp, AdminColors.Outline.copy(alpha = 0.5f), RoundedCornerShape(10.dp))
                            .padding(10.dp),
                    ) {
                        Text("${r.date}  ${r.occurredAt}", fontWeight = FontWeight.SemiBold)
                        Text("${r.subjectTypeLabel}: ${r.subjectName}", fontSize = 13.sp)
                        Text(
                            "${r.sourceLabel} · ${sessionScheduleText(r)} · ${sessionTrainerText(r)}",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                    }
                }
            }
        } else {
            Column(Modifier.fillMaxWidth()) {
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    AdminSortableHeader("Fecha", "date", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("Hora", "time", listPaging.sort, listPaging::onSort, Modifier.weight(0.7f))
                    AdminSortableHeader("Tipo", "type", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
                    AdminSortableHeader("Persona", "name", listPaging.sort, listPaging::onSort, Modifier.weight(1.4f))
                    AdminSortableHeader("Origen", "source", listPaging.sort, listPaging::onSort, Modifier.weight(1.1f))
                    AdminSortableHeader("Clase/Horario", "schedule", listPaging.sort, listPaging::onSort, Modifier.weight(1.2f))
                    AdminSortableHeader("Entrenador", "trainer", listPaging.sort, listPaging::onSort, Modifier.weight(1.1f))
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
                pageItems.forEach { r ->
                    Row(
                        Modifier.fillMaxWidth().padding(vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        Text(r.date, Modifier.weight(0.9f), fontSize = 13.sp, maxLines = 1)
                        Text(r.occurredAt, Modifier.weight(0.7f), fontSize = 13.sp, maxLines = 1)
                        Text(r.subjectTypeLabel, Modifier.weight(0.8f), fontSize = 13.sp, maxLines = 1)
                        Text(r.subjectName, Modifier.weight(1.4f), fontSize = 13.sp, maxLines = 1)
                        Text(r.sourceLabel, Modifier.weight(1.1f), fontSize = 13.sp, maxLines = 1)
                        Text(sessionScheduleText(r), Modifier.weight(1.2f), fontSize = 11.sp, lineHeight = 14.sp, maxLines = 2)
                        Text(sessionTrainerText(r), Modifier.weight(1.1f), fontSize = 13.sp, maxLines = 1)
                    }
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.3f))
                }
            }
        }
    }
}

/** Ancho mínimo de la tabla de ventas: por debajo, se desplaza en horizontal en vez de apretar columnas. */
/** Texto para entrenador vacío: el backend envía "" cuando la venta, orden o inscripción no tiene entrenador. */
private const val SIN_ENTRENADOR = "Sin entrenador"

/** Entrenador de una asistencia: el nombre si lo tiene; el personal no aplica; un socio sin entrenador dice "Sin entrenador". */
private fun sessionTrainerText(r: AdminSessionReportRow): String = when {
    r.trainerName.isNotBlank() -> r.trainerName
    r.subjectType == "STAFF" -> NO_APLICA
    else -> SIN_ENTRENADOR
}

/** Clase/Horario: el personal no aplica; un socio sin clase o servicios muestra "-" (o "Sin membresía" desde el backend). */
private fun sessionScheduleText(r: AdminSessionReportRow): String = r.scheduleTitle.ifBlank {
    if (r.subjectType == "STAFF") NO_APLICA else SIN_DATO
}

private const val NO_APLICA = "No aplica"
private const val SIN_DATO = "-"

private val SALES_TABLE_MIN_WIDTH = 1040.dp

@Composable
private fun SalesReportDetail(
    report: AdminSalesReport?,
    compact: Boolean,
    modifier: Modifier = Modifier,
) {
    val rows = report?.rows.orEmpty()
    val listPaging = rememberAdminListPaging(
        initialSortKey = "date",
        initialDir = AdminSortDir.Desc,
        resetKey = report,
    )
    val pageItems = remember(rows, listPaging.sort, listPaging.page) {
        rows.adminSortedPaged(listPaging.sort, listPaging.page, REPORTS_PAGE_SIZE) { key ->
            when (key) {
                "number" -> { r -> r.saleNumber }
                "customer" -> { r -> r.customerName.lowercase() }
                "product" -> { r -> r.productName.lowercase() }
                "qty" -> { r -> r.qty }
                "stock" -> { r -> r.stockQty }
                "status" -> { r -> r.statusLabel.lowercase() }
                "total" -> { r -> r.totalCents }
                "discount" -> { r -> r.discountCents }
                "paid" -> { r -> r.amountPaidCents }
                "change" -> { r -> r.changeGivenCents }
                "payments" -> { r -> r.paymentsSummary.lowercase() }
                // Pendiente en Bs; el pendiente en $ no se suma con Bs.
                "pending" -> { r -> r.pendingBsCents }
                else -> { r -> r.saleDate }
            }
        }
    }
    if (rows.isEmpty()) {
        Box(modifier) {
            Text("Sin datos para los filtros seleccionados.", color = AdminColors.TextMuted)
        }
        return
    }
    ReportsDetailShell(
        modifier = modifier,
        pageBar = {
            AdminListPaginationBar(
                totalItems = rows.size,
                page = listPaging.page,
                onPageChange = { listPaging.page = it },
                pageSize = REPORTS_PAGE_SIZE,
            )
        },
        footer = {
            ReportsTotalsFooter(
                totals = report?.totalsByMethod.orEmpty(),
                currency = report?.currency ?: GymlyCurrency.CODE,
                compact = compact,
            )
            SalesColumnTotals(rows, report?.currency ?: GymlyCurrency.CODE)
        },
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                pageItems.forEach { r ->
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .border(1.dp, AdminColors.Outline.copy(alpha = 0.5f), RoundedCornerShape(10.dp))
                            .padding(10.dp),
                    ) {
                        Text("${r.saleDate} · ${r.productName.ifBlank { r.itemsSummary }}", fontWeight = FontWeight.SemiBold)
                        Text("${r.customerName} · ×${r.qty}", fontSize = 13.sp)
                        Text(
                            "${r.statusLabel} · stock ${r.stockQty}",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                        Text(salesRowMoney(r), fontWeight = FontWeight.SemiBold)
                        if (r.paymentsSummary.isNotBlank()) {
                            Text(r.paymentsSummary, color = AdminColors.TextMuted, fontSize = 12.sp)
                        }
                        if (r.pendingBsCents > 0L || r.pendingUsdCents > 0L) {
                            Text("Pendiente ${salesPendingText(r)}", color = Color(0xFFE65100), fontSize = 12.sp)
                        }
                        if (r.discountCents > 0L) {
                            Text(
                                "Descuento ${discountCellText(r.discountCents, r.discountUsdCents, r.currency)}",
                                color = AdminColors.TextMuted,
                                fontSize = 12.sp,
                            )
                        }
                        if (r.amountPaidCents > 0 || r.changeGivenCents > 0) {
                            Text(
                                buildString {
                                    if (r.amountPaidCents > 0) {
                                        append("Cobrado ${formatMoney(r.amountPaidCents.toInt(), r.currency)}")
                                    }
                                    if (r.changeGivenCents > 0) {
                                        if (isNotEmpty()) append(" · ")
                                        append("Vuelto ${formatMoney(r.changeGivenCents.toInt(), r.currency)}")
                                    }
                                },
                                color = AdminColors.TextMuted,
                                fontSize = 12.sp,
                            )
                        }
                    }
                }
            }
        } else {
            // Tablet (700–899 dp): la tabla conserva su ancho mínimo y se desplaza en horizontal,
            // para no apretar las 13 columnas.
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                val tableWidth = maxOf(maxWidth, SALES_TABLE_MIN_WIDTH)
                Column(Modifier.horizontalScroll(rememberScrollState())) {
                    Column(Modifier.width(tableWidth)) {
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    AdminSortableHeader("Fecha", "date", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("# de orden", "number", listPaging.sort, listPaging::onSort, Modifier.weight(0.6f))
                    AdminSortableHeader("Producto", "product", listPaging.sort, listPaging::onSort, Modifier.weight(1.6f))
                    AdminSortableHeader("Cant.", "qty", listPaging.sort, listPaging::onSort, Modifier.weight(0.55f))
                    AdminSortableHeader("Cliente", "customer", listPaging.sort, listPaging::onSort, Modifier.weight(1.2f))
                    AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
                    AdminSortableHeader("Total", "total", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("Cobrado", "paid", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("Descuento", "discount", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
                    AdminSortableHeader("Pendiente", "pending", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("Vuelto", "change", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
                    AdminSortableHeader("Pagos", "payments", listPaging.sort, listPaging::onSort, Modifier.weight(1.3f))
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
                pageItems.forEach { r ->
                    Row(
                        Modifier.fillMaxWidth().padding(vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        Text(r.saleDate, Modifier.weight(0.9f), fontSize = 13.sp, maxLines = 1)
                        Text("#${r.saleNumber}", Modifier.weight(0.6f), fontSize = 13.sp, maxLines = 1)
                        Text(r.itemsSummary.ifBlank { r.productName }, Modifier.weight(1.6f), fontSize = 13.sp, maxLines = 1)
                        Text(r.qty.toString(), Modifier.weight(0.55f), fontSize = 13.sp, maxLines = 1)
                        Text(r.customerName, Modifier.weight(1.2f), fontSize = 13.sp, maxLines = 1)
                        Text(r.statusLabel, Modifier.weight(0.8f), fontSize = 13.sp, maxLines = 1)
                        Text(salesRowMoney(r), Modifier.weight(0.9f), fontSize = 13.sp, maxLines = 1)
                        Text(
                            if (r.amountPaidCents > 0) formatMoney(r.amountPaidCents.toInt(), r.currency) else "—",
                            Modifier.weight(0.9f),
                            fontSize = 13.sp,
                            maxLines = 1,
                        )
                        Text(
                            discountCellText(r.discountCents, r.discountUsdCents, r.currency),
                            Modifier.weight(0.8f),
                            fontSize = 13.sp,
                            maxLines = 1,
                        )
                        Text(
                            salesPendingText(r),
                            Modifier.weight(0.9f),
                            fontSize = 12.sp,
                            maxLines = 1,
                            color = if (r.pendingBsCents > 0L || r.pendingUsdCents > 0L) Color(0xFFE65100) else AdminColors.Text,
                        )
                        Text(
                            if (r.changeGivenCents > 0) formatMoney(r.changeGivenCents.toInt(), r.currency) else "—",
                            Modifier.weight(0.8f),
                            fontSize = 13.sp,
                            maxLines = 1,
                            color = if (r.changeGivenCents > 0) Color(0xFFC62828) else AdminColors.Text,
                        )
                        Text(salesPaymentsText(r), Modifier.weight(1.3f), fontSize = 12.sp, maxLines = 1)
                    }
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.3f))
                }
                    }
                }
            }
        }
    }
}

@Composable
private fun InventoryReportDetail(
    report: AdminInventoryReport?,
    compact: Boolean,
    modifier: Modifier = Modifier,
) {
    val rows = report?.rows.orEmpty()
    val listPaging = rememberAdminListPaging(
        initialSortKey = "name",
        resetKey = report,
    )
    val pageItems = remember(rows, listPaging.sort, listPaging.page) {
        rows.adminSortedPaged(listPaging.sort, listPaging.page, REPORTS_PAGE_SIZE) { key ->
            when (key) {
                "category" -> { r -> r.categoryName.lowercase() }
                "qty" -> { r -> r.stockQty }
                "min" -> { r -> r.minStockQty }
                "status" -> { r -> r.stockLevelLabel.lowercase() }
                "price" -> { r -> r.priceCents }
                "usd" -> { r -> r.priceUsdCents }
                "active" -> { r -> r.activeLabel.lowercase() }
                else -> { r -> r.name.lowercase() }
            }
        }
    }
    if (rows.isEmpty()) {
        Box(modifier) {
            Text("Sin productos para los filtros seleccionados.", color = AdminColors.TextMuted)
        }
        return
    }
    ReportsDetailShell(
        modifier = modifier,
        pageBar = {
            AdminListPaginationBar(
                totalItems = rows.size,
                page = listPaging.page,
                onPageChange = { listPaging.page = it },
                pageSize = REPORTS_PAGE_SIZE,
            )
        },
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                pageItems.forEach { r ->
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .border(1.dp, AdminColors.Outline.copy(alpha = 0.5f), RoundedCornerShape(10.dp))
                            .padding(10.dp),
                    ) {
                        Text(r.name, fontWeight = FontWeight.SemiBold)
                        Text(
                            "${r.categoryName} · ${r.stockLevelLabel} · ${r.activeLabel}",
                            color = stockLevelColor(r.stockLevel),
                            fontSize = 12.sp,
                        )
                        Text(
                            "Cantidad ${r.stockQty} · Mínimo ${r.minStockQty} · ${formatMoney(r.priceCents.toInt(), r.currency)}" +
                                if (r.priceUsdCents > 0) " · ${formatMoney(r.priceUsdCents.toInt(), "USD")}" else "",
                            fontSize = 12.sp,
                        )
                        if (r.description != "—") {
                            Text(r.description, color = AdminColors.TextMuted, fontSize = 12.sp, maxLines = 2)
                        }
                    }
                }
            }
        } else {
            Column(Modifier.fillMaxWidth()) {
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    AdminSortableHeader("Producto", "name", listPaging.sort, listPaging::onSort, Modifier.weight(1.4f))
                    AdminSortableHeader("Categoría", "category", listPaging.sort, listPaging::onSort, Modifier.weight(1.1f))
                    AdminSortableHeader("Cantidad", "qty", listPaging.sort, listPaging::onSort, Modifier.weight(0.7f))
                    AdminSortableHeader("Mínimo", "min", listPaging.sort, listPaging::onSort, Modifier.weight(0.65f))
                    AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.85f))
                    AdminSortableHeader("Precio Bs", "price", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("Precio USD", "usd", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("Activo", "active", listPaging.sort, listPaging::onSort, Modifier.weight(0.7f))
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
                pageItems.forEach { r ->
                    Row(
                        Modifier.fillMaxWidth().padding(vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        Text(r.name, Modifier.weight(1.4f), fontSize = 13.sp, maxLines = 2)
                        Text(r.categoryName, Modifier.weight(1.1f), fontSize = 13.sp, maxLines = 2)
                        Text(r.stockQty.toString(), Modifier.weight(0.7f), fontSize = 13.sp, maxLines = 1)
                        Text(r.minStockQty.toString(), Modifier.weight(0.65f), fontSize = 13.sp, maxLines = 1)
                        Text(r.stockLevelLabel, Modifier.weight(0.85f), fontSize = 13.sp, maxLines = 1, color = stockLevelColor(r.stockLevel))
                        Text(formatMoney(r.priceCents.toInt(), r.currency), Modifier.weight(0.9f), fontSize = 13.sp, maxLines = 1)
                        Text(
                            if (r.priceUsdCents > 0) formatMoney(r.priceUsdCents.toInt(), "USD") else "—",
                            Modifier.weight(0.9f),
                            fontSize = 13.sp,
                            maxLines = 1,
                        )
                        Text(r.activeLabel, Modifier.weight(0.7f), fontSize = 13.sp, maxLines = 1)
                    }
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.3f))
                }
            }
        }
    }
}

private fun stockLevelColor(level: String): Color = when (level) {
    "OUT" -> Color(0xFFC62828)
    "LOW" -> Color(0xFFF59E0B)
    else -> Color(0xFF2E7D32)
}

@Composable
private fun EnrollmentsReportDetail(
    report: AdminEnrollmentReport?,
    compact: Boolean,
    modifier: Modifier = Modifier,
) {
    val rows = report?.rows.orEmpty()
    val listPaging = rememberAdminListPaging(
        initialSortKey = "trainer",
        resetKey = report,
    )
    val pageItems = remember(rows, listPaging.sort, listPaging.page) {
        rows.adminSortedPaged(listPaging.sort, listPaging.page, REPORTS_PAGE_SIZE) { key ->
            when (key) {
                "date" -> { r -> r.enrolledAt }
                "plan" -> { r -> r.planName.lowercase() }
                "client" -> { r -> r.clientName.lowercase() }
                "status" -> { r -> r.statusLabel.lowercase() }
                else -> { r -> r.trainerName.lowercase() }
            }
        }
    }
    val byTrainer = remember(rows) {
        rows
            .groupBy { (it.trainerId ?: "") to it.trainerName.ifBlank { SIN_ENTRENADOR } }
            .map { (key, group) ->
                TrainerEnrollmentSummary(
                    trainerId = key.first.takeIf { it.isNotBlank() },
                    trainerName = key.second,
                    total = group.size,
                    clients = group.mapNotNull { it.clientId }.toSet().size.let { distinct ->
                        if (distinct > 0) distinct else group.map { it.clientName }.toSet().size
                    },
                )
            }
            .sortedWith(
                compareByDescending<TrainerEnrollmentSummary> { it.total }
                    .thenBy { it.trainerName.lowercase() },
            )
    }
    if (rows.isEmpty()) {
        Box(modifier) {
            Text("Sin inscritos para los filtros seleccionados.", color = AdminColors.TextMuted)
        }
        return
    }
    ReportsDetailShell(
        modifier = modifier,
        pageBar = {
            AdminListPaginationBar(
                totalItems = rows.size,
                page = listPaging.page,
                onPageChange = { listPaging.page = it },
                pageSize = REPORTS_PAGE_SIZE,
            )
        },
        footer = {
            EnrollmentTrainerSummaryFooter(byTrainer = byTrainer, compact = compact)
        },
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                pageItems.forEach { r ->
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .border(1.dp, AdminColors.Outline.copy(alpha = 0.5f), RoundedCornerShape(10.dp))
                            .padding(10.dp),
                    ) {
                        Text(r.clientName, fontWeight = FontWeight.SemiBold)
                        Text("${r.planName} · ${r.trainerName.ifBlank { SIN_ENTRENADOR }}", color = AdminColors.TextMuted, fontSize = 12.sp)
                        Text("${r.enrolledAt} · ${r.statusLabel}", fontSize = 12.sp)
                    }
                }
            }
        } else {
            Column(Modifier.fillMaxWidth()) {
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    AdminSortableHeader("Fecha", "date", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("Cliente", "client", listPaging.sort, listPaging::onSort, Modifier.weight(1.4f))
                    AdminSortableHeader("Plan", "plan", listPaging.sort, listPaging::onSort, Modifier.weight(1.2f))
                    AdminSortableHeader("Entrenador", "trainer", listPaging.sort, listPaging::onSort, Modifier.weight(1.2f))
                    AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
                pageItems.forEach { r ->
                    Row(
                        Modifier.fillMaxWidth().padding(vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        Text(r.enrolledAt, Modifier.weight(0.9f), fontSize = 13.sp, maxLines = 1)
                        Text(r.clientName, Modifier.weight(1.4f), fontSize = 13.sp, maxLines = 1)
                        Text(r.planName, Modifier.weight(1.2f), fontSize = 13.sp, maxLines = 1)
                        Text(r.trainerName.ifBlank { SIN_ENTRENADOR }, Modifier.weight(1.2f), fontSize = 13.sp, maxLines = 1)
                        Text(r.statusLabel, Modifier.weight(0.9f), fontSize = 13.sp, maxLines = 1)
                    }
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.3f))
                }
            }
        }
    }
}

private data class TrainerEnrollmentSummary(
    val trainerId: String?,
    val trainerName: String,
    val total: Int,
    val clients: Int,
)

@Composable
private fun EnrollmentTrainerSummaryFooter(
    byTrainer: List<TrainerEnrollmentSummary>,
    compact: Boolean,
) {
    if (byTrainer.isEmpty()) return
    val trainerSort = rememberAdminListPaging(initialSortKey = "total", initialDir = AdminSortDir.Desc)
    val sortedByTrainer = remember(byTrainer, trainerSort.sort) {
        byTrainer.sortedWith { a, b ->
            val cmp = when (trainerSort.sort.key) {
                "clients" -> a.clients.compareTo(b.clients)
                "total" -> a.total.compareTo(b.total)
                else -> a.trainerName.lowercase().compareTo(b.trainerName.lowercase())
            }
            if (trainerSort.sort.dir == AdminSortDir.Asc) cmp else -cmp
        }
    }
    Spacer(Modifier.height(14.dp))
    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.45f))
    Spacer(Modifier.height(12.dp))
    Text(
        "Resumen por entrenador",
        fontWeight = FontWeight.Bold,
        fontSize = 14.sp,
        color = AdminColors.Text,
    )
    Text(
        "Total de inscritos atendidos según los filtros aplicados.",
        color = AdminColors.TextMuted,
        fontSize = 12.sp,
        modifier = Modifier.padding(top = 2.dp, bottom = 10.dp),
    )
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            sortedByTrainer.forEach { item ->
                Row(
                    Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(10.dp))
                        .background(AdminColors.Surface)
                        .border(1.dp, AdminColors.Outline.copy(alpha = 0.45f), RoundedCornerShape(10.dp))
                        .padding(horizontal = 12.dp, vertical = 10.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(Modifier.weight(1f)) {
                        Text(item.trainerName.ifBlank { SIN_ENTRENADOR }, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                        Text(
                            "${item.clients} cliente${if (item.clients == 1) "" else "s"}",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                    }
                    Text(
                        "${item.total}",
                        fontWeight = FontWeight.Bold,
                        fontSize = 16.sp,
                        color = AdminColors.Primary,
                    )
                }
            }
            EnrollmentTrainerSummaryTotal(byTrainer)
        }
    } else {
        Column(Modifier.fillMaxWidth()) {
            Row(Modifier.padding(vertical = 6.dp), verticalAlignment = Alignment.CenterVertically) {
                AdminSortableHeader("Entrenador", "trainer", trainerSort.sort, trainerSort::onSort, Modifier.weight(1f))
                AdminSortableHeader("Clientes", "clients", trainerSort.sort, trainerSort::onSort, Modifier.width(120.dp))
                AdminSortableHeader("Atendidos", "total", trainerSort.sort, trainerSort::onSort, Modifier.width(120.dp))
            }
            HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
            sortedByTrainer.forEach { item ->
                Row(Modifier.padding(vertical = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                    Text(item.trainerName.ifBlank { SIN_ENTRENADOR }, Modifier.weight(1f), fontSize = 13.sp)
                    Text("${item.clients}", Modifier.width(90.dp), fontSize = 13.sp)
                    Text(
                        "${item.total}",
                        Modifier.width(90.dp),
                        fontSize = 13.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = AdminColors.Primary,
                    )
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.3f))
            }
            EnrollmentTrainerSummaryTotal(byTrainer)
        }
    }
}

@Composable
private fun EnrollmentTrainerSummaryTotal(byTrainer: List<TrainerEnrollmentSummary>) {
    val total = byTrainer.sumOf { it.total }
    Row(
        Modifier
            .fillMaxWidth()
            .padding(top = 8.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            if (byTrainer.size == 1) "Total del entrenador" else "Total general",
            fontWeight = FontWeight.SemiBold,
            fontSize = 13.sp,
        )
        Text(
            "$total inscrito${if (total == 1) "" else "s"}",
            fontWeight = FontWeight.Bold,
            fontSize = 14.sp,
            color = AdminColors.Primary,
        )
    }
}

@Composable
private fun MembershipsReportDetail(
    report: AdminMembershipReport?,
    compact: Boolean,
    modifier: Modifier = Modifier,
) {
    val rows = report?.rows.orEmpty()
    val listPaging = rememberAdminListPaging(
        initialSortKey = "client",
        resetKey = report,
    )
    val pageItems = remember(rows, listPaging.sort, listPaging.page) {
        rows.adminSortedPaged(listPaging.sort, listPaging.page, REPORTS_PAGE_SIZE) { key ->
            when (key) {
                "plan" -> { r -> r.planName.lowercase() }
                "trainer" -> { r -> r.trainerName.lowercase() }
                "status" -> { r -> r.statusLabel.lowercase() }
                "method" -> { r -> r.methodLabel.lowercase() }
                "starts" -> { r -> r.startsAt }
                "ends" -> { r -> r.endsAt.orEmpty() }
                "price" -> { r -> r.priceCents }
                "paid" -> { r -> r.paidCents }
                "discount" -> { r -> r.discountCents }
                "pending" -> { r -> r.pendingBsCents }
                "change" -> { r -> r.changeGivenCents }
                else -> { r -> r.clientName.lowercase() }
            }
        }
    }
    if (rows.isEmpty()) {
        Box(modifier) {
            Text("Sin datos para los filtros seleccionados.", color = AdminColors.TextMuted)
        }
        return
    }
    ReportsDetailShell(
        modifier = modifier,
        pageBar = {
            AdminListPaginationBar(
                totalItems = rows.size,
                page = listPaging.page,
                onPageChange = { listPaging.page = it },
                pageSize = REPORTS_PAGE_SIZE,
            )
        },
        footer = {
            ReportsTotalsFooter(
                totals = report?.totalsByMethod.orEmpty(),
                currency = report?.currency ?: GymlyCurrency.CODE,
                compact = compact,
            )
        },
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                pageItems.forEach { r ->
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .border(1.dp, AdminColors.Outline.copy(alpha = 0.5f), RoundedCornerShape(10.dp))
                            .padding(10.dp),
                    ) {
                        Text(r.clientName, fontWeight = FontWeight.SemiBold)
                        Text("${r.planName} · ${r.statusLabel}", color = AdminColors.TextMuted, fontSize = 12.sp)
                        Text(r.trainerName.ifBlank { SIN_ENTRENADOR }, color = AdminColors.TextMuted, fontSize = 12.sp)
                        Text(
                            "${r.methodLabel} · ${r.startsAt} → ${r.endsAt ?: "—"} · ${membershipRowMoney(r)}",
                            fontSize = 12.sp,
                        )
                        if (membershipPaidText(r) != "—" || r.discountCents > 0L || membershipPendingText(r) != "—") {
                            Text(
                                buildList {
                                    if (membershipPaidText(r) != "—") add("Cobrado ${membershipPaidText(r)}")
                                    if (r.discountCents > 0L) add("Descuento ${discountCellText(r.discountCents, r.discountUsdCents, r.currency)}")
                                    if (membershipPendingText(r) != "—") add("Pendiente ${membershipPendingText(r)}")
                                }.joinToString(" · "),
                                color = AdminColors.TextMuted,
                                fontSize = 12.sp,
                            )
                        }
                    }
                }
            }
        } else {
            Column(Modifier.fillMaxWidth()) {
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    AdminSortableHeader("Cliente", "client", listPaging.sort, listPaging::onSort, Modifier.weight(1.4f))
                    AdminSortableHeader("Plan", "plan", listPaging.sort, listPaging::onSort, Modifier.weight(1.15f))
                    AdminSortableHeader("Entrenador", "trainer", listPaging.sort, listPaging::onSort, Modifier.weight(1.1f))
                    AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
                    AdminSortableHeader("Método", "method", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
                    AdminSortableHeader("Inicio plan", "starts", listPaging.sort, listPaging::onSort, Modifier.weight(0.95f))
                    AdminSortableHeader("Vencimiento", "ends", listPaging.sort, listPaging::onSort, Modifier.weight(0.95f))
                    AdminSortableHeader("Precio", "price", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("Cobrado", "paid", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("Descuento", "discount", listPaging.sort, listPaging::onSort, Modifier.weight(0.85f))
                    AdminSortableHeader("Pendiente", "pending", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("Vuelto", "change", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
                pageItems.forEach { r ->
                    Row(
                        Modifier.fillMaxWidth().padding(vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        Text(r.clientName, Modifier.weight(1.4f), fontSize = 13.sp, maxLines = 1)
                        Text(r.planName, Modifier.weight(1.15f), fontSize = 13.sp, maxLines = 1)
                        Text(r.trainerName.ifBlank { SIN_ENTRENADOR }, Modifier.weight(1.1f), fontSize = 13.sp, maxLines = 1)
                        Text(r.statusLabel, Modifier.weight(0.8f), fontSize = 13.sp, maxLines = 1)
                        Text(r.methodLabel, Modifier.weight(1f), fontSize = 13.sp, maxLines = 1)
                        Text(r.startsAt, Modifier.weight(0.95f), fontSize = 13.sp, maxLines = 1)
                        Text(r.endsAt ?: "—", Modifier.weight(0.95f), fontSize = 13.sp, maxLines = 1)
                        Text(membershipRowMoney(r), Modifier.weight(0.9f), fontSize = 13.sp, maxLines = 1)
                        Text(
                            membershipPaidText(r),
                            Modifier.weight(0.9f),
                            fontSize = 13.sp,
                            maxLines = 1,
                        )
                        Text(
                            discountCellText(r.discountCents, r.discountUsdCents, r.currency),
                            Modifier.weight(0.85f),
                            fontSize = 13.sp,
                            maxLines = 1,
                        )
                        Text(
                            membershipPendingText(r),
                            Modifier.weight(0.9f),
                            fontSize = 12.sp,
                            maxLines = 1,
                            color = if (r.pendingBsCents > 0L || r.pendingUsdCents > 0L) Color(0xFFE65100) else AdminColors.Text,
                        )
                        Text(
                            if (r.changeGivenCents > 0L) formatMoney(r.changeGivenCents.toInt(), r.currency) else "—",
                            Modifier.weight(0.8f),
                            fontSize = 13.sp,
                            maxLines = 1,
                        )
                    }
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.3f))
                }
            }
        }
    }
}

private data class CashMethodColumn(val method: String, val label: String)

/** Métodos en dólares en recepción: el cuadre muestra USD, no el Bs aplicado al plan. */
private fun isUsdCashMethod(method: String): Boolean {
    val code = method.trim().uppercase()
    return code == "DIVISAS" || code == "USDT" || code == "ZELLE"
}

/**
 * Aporte de un método $ al cuadre (Bs, USD).
 * Preferir foreignUsdCents. Si solo hay Bs (dato legado): con tasa, convertir con BCV; sin tasa (interruptor
 * apagado), el monto queda en Bs tal como se registró, sin convertir.
 */
private fun usdMethodCashContribution(
    foreignUsdCents: Long?,
    amountCents: Long,
    quote: AdminExchangeRateQuote?,
): Pair<Long, Long> {
    val usd = foreignUsdCents?.takeIf { it != 0L } ?: 0L
    if (usd != 0L) return 0L to usd
    if (amountCents <= 0L) return 0L to 0L
    val converted = quote?.let { convertVesToUsdLocally(amountCents, it) }?.takeIf { it > 0 }
        ?: return amountCents to 0L
    return 0L to converted
}

private data class CashDisplayAmount(val cents: Long, val currency: String, val secondaryUsdCents: Long = 0L) {
    fun formatLabel(): String {
        if (currency == "MIXED" && secondaryUsdCents != 0L) {
            return formatMoney(cents.toInt(), GymlyCurrency.CODE) +
                " · " +
                formatMoney(secondaryUsdCents.toInt(), "USD")
        }
        return formatMoney(cents.toInt(), currency)
    }
}

/** Monto corto para celdas de tabla: "Bs 600", "$2", "-Bs 2500,50". */
private fun compactCashCents(cents: Long, currency: String): String {
    val sign = if (cents < 0) "-" else ""
    val abs = kotlin.math.abs(cents)
    val whole = abs / 100
    val frac = abs % 100
    val number = if (frac == 0L) "$whole" else "$whole,${frac.toString().padStart(2, '0')}"
    val prefix = if (currency == "USD") "\$" else "Bs "
    return sign + prefix + number
}

/** Total de una fila del cuadre en una sola línea: "Bs 600 · $2"; sin monto = "—". */
private fun CashDisplayAmount.compactLabel(): String {
    if (cents == 0L && secondaryUsdCents == 0L) return "—"
    if (currency == "MIXED" && secondaryUsdCents != 0L) {
        return compactCashCents(cents, GymlyCurrency.CODE) + " · " + compactCashCents(secondaryUsdCents, "USD")
    }
    return compactCashCents(cents, currency)
}

/** Entradas brutas del cuadre: suma de celdas del detalle (sin vuelto ni monedero). */
private fun AdminCashReconciliationReport.cashInBsUsd(
    quote: AdminExchangeRateQuote? = null,
): Pair<Long, Long> {
    if (rows.isEmpty()) return totalInCents to 0L
    var bs = 0L
    var usd = 0L
    rows.forEach { row ->
        val amounts = row.methodAmounts.ifEmpty {
            listOf(AdminCashMethodAmount(row.method, row.methodLabel, row.amountCents, row.foreignUsdCents))
        }
        amounts.forEach { cell ->
            if (isCashWalletMethod(cell.method)) return@forEach
            if (isUsdCashMethod(cell.method)) {
                val (extraBs, extraUsd) = usdMethodCashContribution(cell.foreignUsdCents, cell.amountCents, quote)
                bs += extraBs
                usd += extraUsd
            } else {
                bs += cell.amountCents
            }
        }
    }
    return bs to usd
}

/** Vuelto del cuadre: suma de changeGivenCents de las filas. */
private fun AdminCashReconciliationReport.cashOutBsUsd(): Pair<Long, Long> {
    val bs = rows.sumOf { it.changeGivenCents.coerceAtLeast(0L) }
    return bs to 0L
}

private fun isCashWalletMethod(method: String): Boolean {
    val code = method.trim().uppercase()
    return code == "WALLET_OVERPAY" || code == "WALLET_APPLY"
}

/** Siempre Bs y $ en líneas separadas (cuadre de caja). */
private fun formatCashBsUsd(bsCents: Long, usdCents: Long, currency: String): String {
    return formatMoney(bsCents.toInt(), currency) + "\n" + formatMoney(usdCents.toInt(), "USD")
}

private fun AdminCashReconciliationRow.displayForMethod(
    method: String,
    quote: AdminExchangeRateQuote? = null,
): CashDisplayAmount? {
    val cell = methodAmounts.firstOrNull { it.method.equals(method, ignoreCase = true) }
        ?: takeIf { this.method.equals(method, ignoreCase = true) }?.let {
            AdminCashMethodAmount(it.method, it.methodLabel, it.amountCents, it.foreignUsdCents, it.detail.takeIf { d ->
                it.method.equals("MOBILE_PAYMENT", ignoreCase = true) &&
                    d.isNotBlank() &&
                    !d.startsWith("Orden #") &&
                    d != "Cobro"
            })
        }
        ?: return null
    if (isUsdCashMethod(cell.method)) {
        val (_, usd) = usdMethodCashContribution(cell.foreignUsdCents, cell.amountCents, quote)
        if (usd == 0L && cell.amountCents == 0L) return null
        if (usd == 0L) return null
        return CashDisplayAmount(usd, "USD")
    }
    if (cell.amountCents == 0L) return null
    return CashDisplayAmount(cell.amountCents, currency)
}

private fun AdminCashReconciliationRow.mobileReferenceForMethod(method: String): String? {
    if (!method.equals("MOBILE_PAYMENT", ignoreCase = true)) return null
    val fromCells = cellsForMethod(method)
        .mapNotNull { it.reference?.trim()?.takeIf { ref -> ref.isNotEmpty() } }
        .distinct()
    // Solo la referencia del pago: nunca el detalle de la venta (productos).
    return fromCells.takeIf { it.isNotEmpty() }?.joinToString(" · ")
}

private fun AdminCashReconciliationRow.displayTotal(
    quote: AdminExchangeRateQuote? = null,
): CashDisplayAmount {
    val amounts = methodAmounts.ifEmpty {
        listOf(AdminCashMethodAmount(method, methodLabel, amountCents, foreignUsdCents))
    }
    var bs = 0L
    var usd = 0L
    amounts.forEach { cell ->
        if (isUsdCashMethod(cell.method)) {
            val (extraBs, extraUsd) = usdMethodCashContribution(cell.foreignUsdCents, cell.amountCents, quote)
            bs += extraBs
            usd += extraUsd
        } else {
            bs += cell.amountCents
        }
    }
    if (bs != 0L && usd != 0L) {
        return CashDisplayAmount(bs, "MIXED", usd)
    }
    if (usd != 0L) {
        return CashDisplayAmount(usd, "USD")
    }
    return CashDisplayAmount(bs, currency)
}

/** Métodos como columnas: orden del backend / habilitados, sin columna «Método» suelta ni vuelto. */
private fun cashMethodColumns(report: AdminCashReconciliationReport): List<CashMethodColumn> {
    val fromTotals = report.totalsByMethod
        .filter {
            !it.method.equals("TOTAL", ignoreCase = true) &&
                !it.method.equals("CHANGE", ignoreCase = true)
        }
        .map { CashMethodColumn(it.method, it.methodLabel) }
    if (fromTotals.isNotEmpty()) return fromTotals
    val preferred = AdminPaymentMethod.adminChoices().map { CashMethodColumn(it.name, it.labelEs()) }
    val extras = report.rows
        .flatMap { row ->
            row.methodAmounts.ifEmpty {
                listOf(AdminCashMethodAmount(row.method, row.methodLabel, row.amountCents, row.foreignUsdCents))
            }
        }
        .map { CashMethodColumn(it.method, it.methodLabel) }
        .filter { row ->
            !row.method.equals("CHANGE", ignoreCase = true) &&
                preferred.none { it.method.equals(row.method, ignoreCase = true) }
        }
        .distinctBy { it.method.uppercase() }
    return preferred + extras
}

private fun AdminCashReconciliationRow.cellsForMethod(method: String): List<AdminCashMethodAmount> {
    val amounts = methodAmounts.ifEmpty {
        listOf(AdminCashMethodAmount(this.method, methodLabel, amountCents, foreignUsdCents))
    }
    return amounts.filter { it.method.equals(method, ignoreCase = true) }
}

/** Suma de celdas del detalle (todas las filas): el pie debe coincidir con lo visible. */
private fun cashMethodAggFromRows(
    rows: List<AdminCashReconciliationRow>,
    method: String,
    quote: AdminExchangeRateQuote? = null,
): Pair<Long, Long> {
    var bs = 0L
    var usd = 0L
    rows.forEach { row ->
        row.cellsForMethod(method).forEach { cell ->
            if (isUsdCashMethod(method)) {
                val (extraBs, extraUsd) = usdMethodCashContribution(cell.foreignUsdCents, cell.amountCents, quote)
                bs += extraBs
                usd += extraUsd
            } else {
                bs += cell.amountCents
            }
        }
    }
    return bs to usd
}

/** Pie de columna: métodos $ siempre en USD. */
private fun cashMethodTotalDisplayFromAgg(
    method: String,
    bsCents: Long,
    usdCents: Long,
    currency: String,
): CashDisplayAmount {
    if (isUsdCashMethod(method)) {
        return CashDisplayAmount(usdCents, "USD")
    }
    if (usdCents != 0L) return CashDisplayAmount(usdCents, "USD")
    return CashDisplayAmount(bsCents, currency)
}

@Composable
private fun CashReconciliationReportDetail(
    report: AdminCashReconciliationReport?,
    exchangeQuote: AdminExchangeRateQuote?,
    compact: Boolean,
    modifier: Modifier = Modifier,
) {
    val rows = report?.rows.orEmpty()
    if (report == null || rows.isEmpty()) {
        Box(modifier) {
            Text("Sin movimientos para el día seleccionado.", color = AdminColors.TextMuted)
        }
        return
    }
    val methods = cashMethodColumns(report)
    val currency = report.currency
    var clientQuery by remember(report) { mutableStateOf("") }
    val filteredRows = remember(rows, clientQuery) {
        val q = clientQuery.trim()
        if (q.isEmpty()) rows
        else rows.filter { it.clientName.contains(q, ignoreCase = true) }
    }
    // Pie = suma de celdas del detalle (día completo, sin filtrar búsqueda); Zelle/USDT/Divisas siempre en $.
    val methodAggs = remember(rows, methods, exchangeQuote) {
        methods.associate { col ->
            col.method to cashMethodAggFromRows(rows, col.method, exchangeQuote)
        }
    }
    var totalBsCents = 0L
    var totalUsdCents = 0L
    methodAggs.values.forEach { (bs, usd) ->
        totalBsCents += bs
        totalUsdCents += usd
    }
    val totalChangeCents = rows.sumOf { it.changeGivenCents.coerceAtLeast(0L) }
    // Totales del día completo (no solo la página): Bs − vuelto.
    val netBsCents = totalBsCents - totalChangeCents
    val listPaging = rememberAdminListPaging(
        initialSortKey = "time",
        initialDir = AdminSortDir.Asc,
        resetKey = report to clientQuery,
    )
    val pageItems = remember(filteredRows, listPaging.sort, listPaging.page, exchangeQuote) {
        filteredRows.adminSortedPaged(listPaging.sort, listPaging.page, REPORTS_PAGE_SIZE) { key ->
            cashRowSortSelector(key, exchangeQuote)
        }
    }

    ReportsDetailShell(
        modifier = modifier,
        // Sin altura fija: las filas multi-método son más altas y se recortaban
        // (p. ej. de Mariangel saltaba a Yenire y no se veía Juseth).
        pageBar = {
            Column(Modifier.fillMaxWidth()) {
                OutlinedTextField(
                    value = clientQuery,
                    onValueChange = { clientQuery = it },
                    modifier = Modifier.fillMaxWidth().padding(bottom = 6.dp),
                    singleLine = true,
                    label = { Text("Buscar cliente") },
                    placeholder = { Text("Ej. JUSETH") },
                )
                AdminListPaginationBar(
                    totalItems = filteredRows.size,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                    pageSize = REPORTS_PAGE_SIZE,
                )
            }
        },
        footer = {
            Column(Modifier.fillMaxWidth().padding(top = 4.dp)) {
                Text("Totales por método", fontWeight = FontWeight.Bold, fontSize = 13.sp, color = AdminColors.Primary)
                Spacer(Modifier.height(4.dp))
                if (compact) {
                    methods.forEach { col ->
                        val (bs, usd) = methodAggs[col.method] ?: (0L to 0L)
                        val display = cashMethodTotalDisplayFromAgg(col.method, bs, usd, currency)
                        if (display.cents == 0L) return@forEach
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                            Text(col.label, fontSize = 12.sp)
                            Text(
                                formatMoney(display.cents.toInt(), display.currency),
                                fontWeight = FontWeight.SemiBold,
                                fontSize = 12.sp,
                                color = if (display.cents < 0) AdminColors.Error else AdminColors.Primary,
                            )
                        }
                    }
                    HorizontalDivider(Modifier.padding(vertical = 4.dp), color = AdminColors.Outline.copy(alpha = 0.5f))
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("Vuelto", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                        Text(
                            if (totalChangeCents > 0) {
                                formatMoney((-totalChangeCents).toInt(), currency)
                            } else {
                                "—"
                            },
                            fontWeight = FontWeight.Bold,
                            fontSize = 13.sp,
                            color = if (totalChangeCents > 0) AdminColors.Error else AdminColors.TextMuted,
                        )
                    }
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("Total Bs", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                        Text(
                            formatMoney(netBsCents.toInt(), currency),
                            fontWeight = FontWeight.Bold,
                            fontSize = 13.sp,
                            color = if (netBsCents < 0) AdminColors.Error else AdminColors.Primary,
                        )
                    }
                    if (totalUsdCents != 0L) {
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                            Text("Total USD", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                            Text(
                                formatMoney(totalUsdCents.toInt(), "USD"),
                                fontWeight = FontWeight.Bold,
                                fontSize = 13.sp,
                                color = AdminColors.Primary,
                            )
                        }
                    }
                } else {
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .horizontalScroll(rememberScrollState())
                            .background(AdminColors.Primary.copy(alpha = 0.08f))
                            .padding(vertical = 6.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        Text("Totales", Modifier.width(64.dp), fontWeight = FontWeight.Bold, fontSize = 13.sp, color = AdminColors.Primary)
                        Spacer(Modifier.width(150.dp))
                        Spacer(Modifier.width(130.dp))
                        methods.forEach { col ->
                            val (bs, usd) = methodAggs[col.method] ?: (0L to 0L)
                            val display = cashMethodTotalDisplayFromAgg(col.method, bs, usd, currency)
                            Text(
                                if (display.cents == 0L) "—" else formatMoney(display.cents.toInt(), display.currency),
                                Modifier.width(110.dp),
                                fontWeight = FontWeight.Bold,
                                fontSize = 13.sp,
                                color = if (display.cents < 0) AdminColors.Error else AdminColors.Primary,
                            )
                        }
                        Text(
                            if (totalChangeCents > 0) {
                                formatMoney((-totalChangeCents).toInt(), currency)
                            } else {
                                "—"
                            },
                            Modifier.width(100.dp),
                            fontWeight = FontWeight.Bold,
                            fontSize = 13.sp,
                            color = if (totalChangeCents > 0) AdminColors.Error else AdminColors.TextMuted,
                        )
                        Column(Modifier.width(110.dp)) {
                            Text(
                                formatMoney(netBsCents.toInt(), currency),
                                fontWeight = FontWeight.Bold,
                                fontSize = 12.sp,
                                color = if (netBsCents < 0) AdminColors.Error else AdminColors.Primary,
                            )
                            if (totalUsdCents != 0L) {
                                Text(
                                    formatMoney(totalUsdCents.toInt(), "USD"),
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 12.sp,
                                    color = AdminColors.Primary,
                                )
                            }
                        }
                        Spacer(Modifier.width(220.dp))
                    }
                }
            }
        },
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                pageItems.forEach { r ->
                    val totalDisplay = r.displayTotal(exchangeQuote)
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .border(1.dp, AdminColors.Outline.copy(alpha = 0.5f), RoundedCornerShape(10.dp))
                            .padding(10.dp),
                    ) {
                        Text("${r.timeLabel} · ${r.sourceLabel}", fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                        Text(r.clientName, color = AdminColors.TextMuted, fontSize = 12.sp)
                        CashPayRegisterHint(r)
                        Spacer(Modifier.height(4.dp))
                        methods.forEach { col ->
                            val amount = r.displayForMethod(col.method, exchangeQuote) ?: return@forEach
                            if (amount.cents != 0L) {
                                val mobileRef = r.mobileReferenceForMethod(col.method)
                                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                    Text(col.label, fontSize = 12.sp, color = AdminColors.TextMuted)
                                    Column(horizontalAlignment = Alignment.End) {
                                        Text(
                                            formatMoney(amount.cents.toInt(), amount.currency),
                                            fontSize = 12.sp,
                                            color = if (amount.cents < 0) AdminColors.Error else AdminColors.Text,
                                        )
                                        if (!mobileRef.isNullOrBlank()) {
                                            Text(
                                                mobileRef,
                                                fontSize = 12.sp,
                                                fontWeight = FontWeight.Medium,
                                                color = AdminColors.Primary,
                                            )
                                        }
                                    }
                                }
                            }
                        }
                        if (r.changeGivenCents > 0L) {
                            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                Text("Vuelto", fontSize = 12.sp, color = AdminColors.TextMuted)
                                Text(
                                    formatMoney((-r.changeGivenCents).toInt(), r.currency),
                                    fontSize = 12.sp,
                                    color = AdminColors.Error,
                                    fontWeight = FontWeight.SemiBold,
                                )
                            }
                        }
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                            Text("Total", fontWeight = FontWeight.SemiBold, fontSize = 12.sp)
                            Text(
                                totalDisplay.formatLabel(),
                                fontWeight = FontWeight.SemiBold,
                                fontSize = 12.sp,
                                color = if (totalDisplay.cents < 0) AdminColors.Error else AdminColors.Primary,
                            )
                        }
                        Text(r.detail, color = AdminColors.TextMuted, fontSize = 12.sp)
                    }
                }
            }
        } else {
            Column(Modifier.fillMaxWidth()) {
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    AdminSortableHeader("Hora", "time", listPaging.sort, listPaging::onSort, Modifier.weight(0.7f))
                    AdminSortableHeader("Origen", "source", listPaging.sort, listPaging::onSort, Modifier.weight(1.1f))
                    AdminSortableHeader("Cliente", "client", listPaging.sort, listPaging::onSort, Modifier.weight(1.2f))
                    methods.forEach { col ->
                        AdminSortableHeader(col.label, "m:${col.method}", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
                    }
                    AdminSortableHeader("Vuelto", "change", listPaging.sort, listPaging::onSort, Modifier.weight(0.85f))
                    AdminSortableHeader("Total", "total", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
                    AdminSortableHeader("Detalle", "detail", listPaging.sort, listPaging::onSort, Modifier.weight(1.4f))
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
                pageItems.forEach { r ->
                    val totalDisplay = r.displayTotal(exchangeQuote)
                    Row(
                        Modifier.fillMaxWidth().padding(vertical = 8.dp),
                        verticalAlignment = Alignment.Top,
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                    ) {
                        Text(r.timeLabel, Modifier.weight(0.7f), fontSize = 13.sp, maxLines = 1)
                        Text(r.sourceLabel, Modifier.weight(1.1f), fontSize = 13.sp, maxLines = 1)
                        Text(r.clientName, Modifier.weight(1.2f), fontSize = 13.sp, maxLines = 1)
                        methods.forEach { col ->
                            val amount = r.displayForMethod(col.method, exchangeQuote)
                            val mobileRef = r.mobileReferenceForMethod(col.method)
                                ?.takeIf { amount != null && amount.cents != 0L }
                            Column(Modifier.weight(1f)) {
                                Text(
                                    if (amount == null || amount.cents == 0L) {
                                        "—"
                                    } else {
                                        formatMoney(amount.cents.toInt(), amount.currency)
                                    },
                                    fontSize = 13.sp,
                                    maxLines = 1,
                                    color = when {
                                        amount != null && amount.cents < 0 -> AdminColors.Error
                                        amount != null && amount.cents > 0 -> AdminColors.Text
                                        else -> AdminColors.TextMuted
                                    },
                                )
                                if (!mobileRef.isNullOrBlank()) {
                                    Text(
                                        mobileRef,
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.Medium,
                                        color = AdminColors.Primary,
                                        maxLines = 1,
                                    )
                                }
                            }
                        }
                        Text(
                            if (r.changeGivenCents > 0L) {
                                compactCashCents(-r.changeGivenCents, r.currency)
                            } else {
                                "—"
                            },
                            Modifier.weight(0.85f),
                            fontSize = 13.sp,
                            maxLines = 1,
                            fontWeight = if (r.changeGivenCents > 0L) FontWeight.SemiBold else FontWeight.Normal,
                            color = if (r.changeGivenCents > 0L) AdminColors.Error else AdminColors.TextMuted,
                        )
                        Text(
                            totalDisplay.compactLabel(),
                            Modifier.weight(0.9f),
                            fontWeight = FontWeight.SemiBold,
                            fontSize = 13.sp,
                            maxLines = 2,
                            color = if (totalDisplay.cents < 0) AdminColors.Error else AdminColors.Text,
                        )
                        Column(Modifier.weight(1.4f)) {
                            CashPayRegisterHint(r)
                            Text(r.detail, fontSize = 12.sp, color = AdminColors.TextMuted)
                        }
                    }
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.3f))
                }
            }
        }
    }
}

/** Valor por el que se ordena cada columna de Detalle del día; "m:<método>" = monto de ese método. */
private fun cashRowSortSelector(
    key: String,
    quote: AdminExchangeRateQuote?,
): (AdminCashReconciliationRow) -> Comparable<*>? {
    if (key.startsWith("m:")) {
        val method = key.removePrefix("m:")
        return { r -> r.displayForMethod(method, quote)?.cents }
    }
    return when (key) {
        "source" -> { r -> r.sourceLabel }
        "client" -> { r -> r.clientName }
        "change" -> { r -> r.changeGivenCents }
        "total" -> { r -> r.displayTotal(quote).cents }
        "detail" -> { r -> r.detail }
        else -> { r -> r.timeLabel }
    }
}

/** Solo si digitaron otro día: Pago: dd/MM · Registrado: dd/MM HH:mm */
@Composable
private fun CashPayRegisterHint(row: AdminCashReconciliationRow) {
    val registered = row.registeredLabel?.takeIf { it.isNotBlank() } ?: return
    val paid = row.paidDateLabel?.takeIf { it.isNotBlank() } ?: "—"
    Row(verticalAlignment = Alignment.CenterVertically) {
        Text("Pago: $paid · ", fontSize = 12.sp, color = AdminColors.TextMuted)
        Text(
            "Registrado: $registered",
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
            color = Color(0xFFE65100),
        )
    }
}
